import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import AnchoredPopover from "../components/AnchoredPopover";
import ExtractLoader from "../components/ExtractLoader";
import { useParams, useNavigate } from "react-router-dom";
import {
  requestsApi,
  jobsApi,
  outputDestinationsApi,
  requestConnectorsApi,
} from "../services/api";
import {
  formatTimeAgo as formatTimeAgoUtil,
  formatDateTime,
  formatAuditTimestamp,
} from "../utils/dateUtils";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useDisplayPreferences } from "../context/DisplayPreferencesContext";
import { useFeatures } from "../contexts/FeaturesContext";
import { SHOW_DEV_LOGS } from "../config/runtimeConfig";
import PDFViewer from "../components/PDFViewer";
import DocumentViewer from "../components/DocumentViewer";
import CostBreakdown from "../components/CostBreakdown";
import BCRequestView from "../components/BCRequestView";
import NSRequestView from "../components/NSRequestView";
import NSBidRequestView from "../components/NSBidRequestView";
import NSGenericRequestView from "../components/NSGenericRequestView";
import OpCoBadge from "../components/opco/OpCoBadge";
import { MoveOpCoButton, UnassignedOpCoBanner } from "../components/opco/MoveOpCoDialog";
import RequestPanelTabs from "../components/requestDetail/RequestPanelTabs";
import FieldsPanelHeader from "../components/requestDetail/FieldsPanelHeader";
import ActivityLogPanel from "../components/requestDetail/ActivityLogPanel";
import ConnectorDeliveriesPanel from "../components/requestDetail/ConnectorDeliveriesPanel";
import { formatAuditLogsForActivity } from "../utils/auditLog";
import { canonicalNsEntityType } from "../utils/nsEntityLabels";
import { plainReason } from "../utils/mappingConsistency";
import { isRequestLocked } from "../utils/requestStatus";
import { RequestDetailSkeleton } from "../components/PageSkeletons";
import {
  activeCandidate,
  fieldSourceBadge,
  isEmailSourced,
  valueCandidates,
} from "../utils/fieldValueSources";
import {
  ArrowLeft,
  FileText,
  BrainCircuit,
  Check,
  X,
  RefreshCw,
  Save,
  AlertCircle,
  Clock,
  ChevronDown,
  CheckCircle,
  Bot,
  FileSpreadsheet,
  File,
  Layers,
  History,
  Loader2,
  Sparkles,
  Mail,
  XCircle,
  SendHorizonal,
  Activity,
  Server,
  Send,
  ClipboardList
} from 'lucide-react'

function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const features = useFeatures();
  const { user: currentUser } = useAuth();
  const userRole = currentUser?.role || "user";
  const isReader = userRole === "reader";
  // Output destination re-export (AC7) is admin-only on the backend
  // (trigger_export is @require_admin, matching every other output
  // destination write action - link/unlink/map/create/update/delete are
  // all admin-only too, none allow template_manager). Gate the button the
  // same way here so a template_manager never sees a control that would
  // 403 when clicked.
  const isAdmin = userRole === "admin";
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editedFields, setEditedFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [activeDocTab, setActiveDocTab] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [highlightedField, setHighlightedField] = useState(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const fieldRowRefs = useRef({});
  const [pulsedFieldId, setPulsedFieldId] = useState(null);
  const [emptyFieldCycleIndex, setEmptyFieldCycleIndex] = useState(0);
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Activity log entries from API
  const [activityLog, setActivityLog] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [editHistory, setEditHistory] = useState([]);

  // Batch audit history map: { fieldId: true } for fields with audit logs
  const [fieldsAuditMap, setFieldsAuditMap] = useState({});

  // Output destination export deliveries (AC7/AC8)
  const [outputDeliveries, setOutputDeliveries] = useState([]);
  const [loadingOutputDeliveries, setLoadingOutputDeliveries] = useState(false);
  // Connectors whose latest delivery of this request failed or was blocked —
  // counted into the Exports tab badge alongside failed file exports.
  const [failedConnectorCount, setFailedConnectorCount] = useState(0);
  const [reExportingDeliveryId, setReExportingDeliveryId] = useState(null);
  const [linkedOutputDestinations, setLinkedOutputDestinations] = useState([]);
  const [manualExportDestinationId, setManualExportDestinationId] = useState("");
  const [manualExporting, setManualExporting] = useState(false);
  const [expandedDeliveryError, setExpandedDeliveryError] = useState(null);
  // Auto-refresh after publish/approve + poll while any delivery is pending
  const outputDeliveriesPublishRefetchRef = useRef(null);
  const outputDeliveriesPollRef = useRef(null);
  const outputDeliveriesPollAttemptsRef = useRef(0);

  // Processing state for popup
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingMinimized, setProcessingMinimized] = useState(false);
  const pollingIntervalRef = useRef(null);
  const processingCardRef = useRef(null);

  // Stuck request auto-cancel state
  const processingStartTimeRef = useRef(null);
  const stuckTimerRef = useRef(null);
  const [stuckMinutes, setStuckMinutes] = useState(0);
  const [stuckCancelling, setStuckCancelling] = useState(false);

  // Document upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const docTabsRef = useRef(null);

  // Document processing banner state (for initial docs still being processed)
  const [documentsStillProcessing, setDocumentsStillProcessing] =
    useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  // A background job finished while the user had unsaved edits, so the
  // auto-refresh was held back (it would replace editedFields wholesale and
  // silently discard them). The user refreshes explicitly instead.
  const [refreshHeldForEdits, setRefreshHeldForEdits] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({
    percent: 0,
    message: "",
  });
  const [processingLog, setProcessingLog] = useState([]);
  const [showProcessingLog, setShowProcessingLog] = useState(false);
  const processingBannerPollRef = useRef(null);
  const processingBannerCompletionHandledRef = useRef(false);
  const processingJobIdRef = useRef(null);
  const processingLogPollRef = useRef(null);
  const logBottomRef = useRef(null);

  // There is no view/edit toggle: a request that the backend allows edits on
  // is directly editable (#490). Reader accounts, a running extraction and
  // requests in a terminal status are still read-only — see isFieldsDisabled.
  const [originalFields, setOriginalFields] = useState([]); // For discard functionality

  // Editable request metadata (title, description, issuer)
  const [editedMetadata, setEditedMetadata] = useState({
    title: "",
    description: "",
    issuer: "",
  });
  const [originalMetadata, setOriginalMetadata] = useState({
    title: "",
    description: "",
    issuer: "",
  });

  // Save reason modal state
  const [showSaveReasonModal, setShowSaveReasonModal] = useState(false);
  const [saveReason, setSaveReason] = useState("");
  const [showSaveModeModal, setShowSaveModeModal] = useState(false);
  const [loadingSaveModeOptions, setLoadingSaveModeOptions] = useState(false);
  const [saveIntent, setSaveIntent] = useState("db");
  const [saveFlowContext, setSaveFlowContext] = useState({
    requireReason: false,
    postSaveAction: null, // null | "publish" | "sendForReview"
    defaultReason: "User saved changes",
  });
  const [pendingAiSaveReason, setPendingAiSaveReason] = useState("");
  const [liveTemplateAiLearningEnabled, setLiveTemplateAiLearningEnabled] =
    useState(false);
  const [showAiLearningAssistModal, setShowAiLearningAssistModal] =
    useState(false);
  const [aiAssistFieldOptions, setAiAssistFieldOptions] = useState([]);
  const [aiAssistSelectedFieldId, setAiAssistSelectedFieldId] = useState(null);
  const [aiAssistSuggestions, setAiAssistSuggestions] = useState([]);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistSelectedRuleContent, setAiAssistSelectedRuleContent] =
    useState("");
  const [aiAssistCustomInstruction, setAiAssistCustomInstruction] =
    useState("");

  // Cancel reason modal state
  const [showCancelReasonModal, setShowCancelReasonModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [sendingForReview, setSendingForReview] = useState(false);

  // Left panel tabs: Fields / Activity Log / AI Operations (replaces the old popup views)
  const [leftPanelTab, setLeftPanelTab] = useState("fields");
  const [templateAiLearningRules, setTemplateAiLearningRules] = useState([]);
  const [aiLearningUsage, setAiLearningUsage] = useState(null);
  const [aiLearningDebugStatus, setAiLearningDebugStatus] = useState("");

  // PDF Viewer panel visibility toggle
  const [isPdfViewerVisible, setIsPdfViewerVisible] = useState(true);

  // Draggable split between Extracted Fields (left) and PDF viewer (right).
  // Value = % width of the left (fields) panel. Default 50/50 split.
  const [leftPanelPct, setLeftPanelPct] = useState(50);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const mainContentRef = useRef(null);

  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    setIsDraggingDivider(true);

    const onMouseMove = (moveEvent) => {
      if (!mainContentRef.current) return;
      const rect = mainContentRef.current.getBoundingClientRect();
      let pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      // Snap fully closed near the edges so panels can completely vanish
      if (pct <= 3) pct = 0;
      if (pct >= 97) pct = 100;
      // Prevent the fields panel from being dragged so narrow that its header
      // (title, search/filter/toggle row) breaks into a cramped, wrapped mess.
      // Only clamp when the panel isn't being fully collapsed (pct === 0).
      else if (pct < 28) pct = 28;
      pct = Math.min(100, Math.max(0, pct));
      setLeftPanelPct(pct);
      // Dragging all the way right collapses the PDF viewer entirely,
      // reusing the existing "Hide Viewer" full-width fields layout.
      if (pct >= 100) {
        setIsPdfViewerVisible(false);
      }
    };
    const onMouseUp = () => {
      setIsDraggingDivider(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Computed: true when request is completed/cancelled
  const isViewOnly =
    request?.status === "completed" || request?.status === "cancelled";

  // Computed: true while any extraction job is still running (or just
  // finished but the page hasn't been refreshed to load its results) —
  // covers both the initial extraction (status 'pending'/'processing', see
  // showProcessingModal) and a later background job on an already-reviewed
  // request (documentsStillProcessing/processingComplete). Publishing/
  // saving/sending for review while this is true would act on fields that
  // are about to change under the user, so every action button stays
  // disabled until the job finishes and they refresh to see the final data.
  const backgroundJobActive =
    documentsStillProcessing || processingComplete || showProcessingModal;

  // Computed: true when fields must stay read-only — a reader account, an
  // extraction still running, or a terminal status (approved/rejected/failed/
  // completed/cancelled, per isRequestLocked, which mirrors the backend's
  // TERMINAL_REQUEST_STATUSES and would 409 on save). published/
  // pending_approval/in_review are editable directly (#490).
  const isFieldsDisabled =
    isReader || showProcessingModal || isRequestLocked(request?.status);

  // Computed: true when template has mandate_review enabled
  const mandateReview = !!request?.template?.mandate_review;
  // True when this template's review happens in a connected application. The
  // backend blocks non-admin publishing for these; this drives the banner and
  // hides the publish action so nobody is offered a button that will 403.
  const externalReview = !!request?.template_external_review;
  const currentTemplateAiLearningEnabled = liveTemplateAiLearningEnabled;

  const rulesByTemplateFieldId = useMemo(() => {
    const map = {};
    (templateAiLearningRules || []).forEach((rule) => {
      if (!rule?.template_field_id) return;
      map[String(rule.template_field_id)] = rule;
    });
    return map;
  }, [templateAiLearningRules]);

  const buildChangedFieldsPayload = () => {
    const originalById = new Map(
      (originalFields || [])
        .filter((f) => f?.id != null)
        .map((f) => [String(f.id), f]),
    );
    const changed = [];

    (editedFields || []).forEach((field) => {
      if (!field) return;
      const currentValue = field.field_value ?? "";

      if (field.id != null) {
        const original = originalById.get(String(field.id));
        const originalValue = original?.field_value ?? "";
        if (String(currentValue) !== String(originalValue)) {
          changed.push({
            id: field.id,
            template_field_id: field.template_field_id,
            field_name: field.field_name,
            original_field_value: originalValue,
            field_value: currentValue,
          });
        }
        return;
      }

      // New ad-hoc field support: only send if there is a value to save
      if (String(currentValue).trim() !== "") {
        changed.push({
          template_field_id: field.template_field_id,
          field_name: field.field_name,
          original_field_value: "",
          field_value: currentValue,
        });
      }
    });

    return changed;
  };

  const changedFieldsPayload = buildChangedFieldsPayload();
  const changedFieldCount = changedFieldsPayload.length;
  const aiLearningCandidates = changedFieldsPayload.filter(
    (f) =>
      f?.id != null &&
      f?.template_field_id != null &&
      String(f?.field_value ?? "").trim() !== "",
  );
  const aiLearningCandidateCount = aiLearningCandidates.length;

  const extractAiLearningUsage = (requestData, costDetails) => {
    const matchingOp = (costDetails?.operations || []).find(
      (op) =>
        /learning/i.test(op?.name || "") ||
        /learning/i.test(op?.description || ""),
    );
    if (matchingOp) {
      const input = matchingOp?.details?.input_tokens || 0;
      const output = matchingOp?.details?.output_tokens || 0;
      const cost = matchingOp?.cost_usd || 0;
      if (input > 0 || output > 0 || cost > 0) {
        return { input, output, cost };
      }
    }

    const fallback =
      requestData?.last_job?.result_data?.ai_learning_tokens ||
      requestData?.last_job?.result_data?.cost_tracker?.ai_learning_tokens;
    if (fallback) {
      const input = fallback.input ?? fallback.input_tokens ?? 0;
      const output = fallback.output ?? fallback.output_tokens ?? 0;
      const cost = fallback.cost ?? fallback.estimated_cost_usd ?? 0;
      if (input > 0 || output > 0 || cost > 0) {
        return { input, output, cost };
      }
    }

    return null;
  };

  // Fetch audit logs
  const fetchAuditLogs = async () => {
    try {
      setLoadingAuditLogs(true);
      const res = await requestsApi.getAuditLogs(id, { limit: 20 });
      const logs = res.data.audit_logs || [];

      // Transform audit logs to activity log format
      // Filter out low-value entries (INSERTs from extraction, empty changes, null-only values)
      const formattedLogs = formatAuditLogsForActivity(logs);

      setActivityLog(formattedLogs);

      // Transform UPDATE logs to edit history format (filter out null/empty changes)
      const historyItems = logs
        .filter((log) => log.action === "UPDATE")
        .flatMap((log) => {
          const changes = log.changes || [];
          return changes
            .filter((change) => {
              // Only include changes with at least one meaningful value
              const oldVal = change.old_value;
              const newVal = change.new_value;
              return (
                (oldVal !== null && oldVal !== undefined && oldVal !== "") ||
                (newVal !== null && newVal !== undefined && newVal !== "")
              );
            })
            .map((change, idx) => ({
              id: `${log.id}-${idx}`,
              timestamp: formatAuditTimestamp(log.created_at),
              oldValue:
                change.old_value !== null &&
                change.old_value !== undefined &&
                change.old_value !== ""
                  ? String(change.old_value)
                  : "-",
              newValue:
                change.new_value !== null &&
                change.new_value !== undefined &&
                change.new_value !== ""
                  ? String(change.new_value)
                  : "-",
              user: log.created_by_name || "User",
              fieldName: change.display_label || change.field,
            }));
        })
        .slice(0, 10); // Limit to 10 most recent edits

      setEditHistory(historyItems);
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      // Fallback to demo data if API fails
      setActivityLog([
        { id: 1, action: "No recent activity", time: "", icon: "system" },
      ]);
      setEditHistory([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  // Batch fetch audit summary for all fields (single API call)
  const fetchFieldsAuditSummary = async () => {
    try {
      const res = await requestsApi.getFieldsAuditSummary(id);
      setFieldsAuditMap(res.data.fields_with_history || {});
    } catch (err) {
      console.error("Error fetching fields audit summary:", err);
      setFieldsAuditMap({});
    }
  };

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const res = await requestsApi.get(id);
      setRequest(res.data);
      setEditedFields(res.data.fields || []);
      // Store original fields for discard functionality
      setOriginalFields(JSON.parse(JSON.stringify(res.data.fields || [])));

      // Set editable metadata
      const metadata = {
        title: res.data.title || "",
        description: res.data.description || "",
        issuer: res.data.issuer || "",
      };
      setEditedMetadata(metadata);
      setOriginalMetadata(JSON.parse(JSON.stringify(metadata)));

      // Load processing log from last_job if available (persists after job completes)
      if (res.data.last_job?.result_data?.processing_log) {
        setProcessingLog(res.data.last_job.result_data.processing_log);
      }

      if (res.data?.template?.ai_learning_enabled) {
        try {
          const rulesRes = await requestsApi.listAiLearningRules(id);
          setTemplateAiLearningRules(rulesRes.data?.rules || []);
        } catch (rulesErr) {
          console.warn("Failed to load AI learning rules:", rulesErr);
          setTemplateAiLearningRules([]);
        }
      } else {
        setTemplateAiLearningRules([]);
      }

      const hasAiLearningField = (res.data.fields || []).some(
        (f) => f?.source_type === "ai_learning",
      );
      if (hasAiLearningField) {
        try {
          const costRes = await requestsApi.getCostDetails(id);
          setAiLearningUsage(extractAiLearningUsage(res.data, costRes.data));
        } catch (costErr) {
          console.warn("Failed to load AI learning cost data:", costErr);
          setAiLearningUsage(extractAiLearningUsage(res.data, null));
        }
      } else {
        setAiLearningUsage(null);
      }

      setError(null);

      // If this request's template has BC enabled, BCRequestView will handle the render inline
      // Do NOT redirect — stay on /requests/:id

      if (res.data.documents && res.data.documents.length > 0) {
        const pdfDoc = res.data.documents.find(
          (doc) =>
            doc.content_type === "application/pdf" ||
            doc.filename?.endsWith(".pdf"),
        );
        if (pdfDoc) {
          setDocumentId(pdfDoc.id);
        }
      }
    } catch (err) {
      console.error("Error fetching request:", err);
      setError("Failed to load request");
    } finally {
      setLoading(false);
    }
  };

  // Silent poll: fetch request data without loading spinner or PDF remount.
  // Merges status + fields only. When status changes from processing → done, does one full refresh.
  const silentPollRequest = async () => {
    try {
      const res = await requestsApi.get(id);
      const fresh = res.data;

      setRequest((prev) => {
        if (!prev) return fresh;

        // If status changed from processing/pending → something else, let the
        // polling effect handle a full fetchRequest on next cycle
        const statusChanged = prev.status !== fresh.status;

        // Merge: keep the same object identity for documents (so PDFViewer doesn't remount)
        // but update fields, status, metadata, active_job, last_job
        return {
          ...prev,
          status: fresh.status,
          fields: fresh.fields,
          active_job: fresh.active_job,
          last_job: fresh.last_job,
          title: fresh.title,
          description: fresh.description,
          issuer: fresh.issuer,
          updated_at: fresh.updated_at,
          // Only update documents array if the count changed (new doc uploaded)
          documents:
            prev.documents?.length !== fresh.documents?.length
              ? fresh.documents
              : prev.documents,
        };
      });

      // Update fields for the form (only if user hasn't made local edits)
      setEditedFields((prev) => {
        // If the field values are identical to what we already have, skip
        const freshFields = fresh.fields || [];
        if (JSON.stringify(prev) === JSON.stringify(freshFields)) return prev;
        return freshFields;
      });
      setOriginalFields(JSON.parse(JSON.stringify(fresh.fields || [])));

      // Update processing log if available
      if (fresh.last_job?.result_data?.processing_log) {
        setProcessingLog(fresh.last_job.result_data.processing_log);
      }

      const hasAiLearningField = (fresh.fields || []).some(
        (f) => f?.source_type === "ai_learning",
      );
      if (hasAiLearningField) {
        setAiLearningUsage(extractAiLearningUsage(fresh, null));
      }

      setError(null);
    } catch (err) {
      // Silently ignore poll errors — don't blank the page
      console.warn("Silent poll error:", err.message);
    }
  };

  // Fetch output destination export deliveries for this request (AC7/AC8)
  const fetchOutputDeliveries = async () => {
    if (!features.connectors) return;
    try {
      setLoadingOutputDeliveries(true);
      const res = await outputDestinationsApi.listDeliveriesForRequest(id);
      setOutputDeliveries(res.data?.deliveries || []);
    } catch (e) {
      console.error("Failed to load output deliveries", e);
    } finally {
      setLoadingOutputDeliveries(false);
    }
  };

  // Poll fetchOutputDeliveries every ~3s while any delivery is "pending",
  // capped at 5 attempts (~15s) so we never poll forever if a delivery gets
  // stuck. Mirrors the existing processingBannerPollRef job-status polling
  // pattern used elsewhere on this page.
  const MAX_OUTPUT_DELIVERY_POLL_ATTEMPTS = 5;
  const OUTPUT_DELIVERY_POLL_INTERVAL_MS = 3000;

  useEffect(() => {
    const hasPending = outputDeliveries.some((d) => d.status === "pending");

    if (hasPending && !outputDeliveriesPollRef.current) {
      outputDeliveriesPollAttemptsRef.current = 0;
      outputDeliveriesPollRef.current = setInterval(async () => {
        outputDeliveriesPollAttemptsRef.current += 1;
        await fetchOutputDeliveries();
        if (
          outputDeliveriesPollAttemptsRef.current >=
          MAX_OUTPUT_DELIVERY_POLL_ATTEMPTS
        ) {
          clearInterval(outputDeliveriesPollRef.current);
          outputDeliveriesPollRef.current = null;
        }
      }, OUTPUT_DELIVERY_POLL_INTERVAL_MS);
    } else if (!hasPending && outputDeliveriesPollRef.current) {
      clearInterval(outputDeliveriesPollRef.current);
      outputDeliveriesPollRef.current = null;
    }

    return () => {
      if (outputDeliveriesPollRef.current) {
        clearInterval(outputDeliveriesPollRef.current);
        outputDeliveriesPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputDeliveries]);

  // Cleanup the one-shot post-publish refetch timer on unmount
  useEffect(() => {
    return () => {
      if (outputDeliveriesPublishRefetchRef.current) {
        clearTimeout(outputDeliveriesPublishRefetchRef.current);
        outputDeliveriesPublishRefetchRef.current = null;
      }
    };
  }, []);

  // Destinations linked to this request's template. Needed because a
  // destination linked AFTER a request was approved has no delivery row, so
  // there was previously nothing in the UI to export it from - the backend
  // has always supported it (POST /output-destinations/<id>/export/<req>).
  const fetchLinkedOutputDestinations = useCallback(async () => {
    if (!features.connectors) return;
    const templateId = request?.template?.id;
    if (!templateId) return;
    try {
      const resp = await outputDestinationsApi.listForTemplate(templateId);
      setLinkedOutputDestinations(resp.data?.destinations || []);
    } catch {
      setLinkedOutputDestinations([]);
    }
  }, [features.connectors, request?.template?.id]);

  useEffect(() => {
    if (isAdmin) fetchLinkedOutputDestinations();
  }, [isAdmin, fetchLinkedOutputDestinations]);

  const handleManualExport = async () => {
    if (!manualExportDestinationId) return;
    try {
      setManualExporting(true);
      await outputDestinationsApi.triggerExport(manualExportDestinationId, id);
      toast.success(
        "Export triggered",
        "The request is being exported to the selected destination.",
      );
      setManualExportDestinationId("");
      await fetchOutputDeliveries();
    } catch (e) {
      console.error(e);
      toast.error(
        "Export failed",
        e?.response?.data?.error ||
          "Could not export this request to the destination.",
      );
    } finally {
      setManualExporting(false);
    }
  };

  const handleReExportDelivery = async (delivery) => {
    if (!delivery?.destination_id) {
      toast.error(
        "Re-export failed",
        "This delivery has no destination reference (the original destination may have been deleted).",
      );
      return;
    }
    try {
      setReExportingDeliveryId(delivery.id);
      await outputDestinationsApi.triggerExport(delivery.destination_id, id);
      toast.success(
        "Export triggered",
        "The request is being re-exported to the destination.",
      );
      await fetchOutputDeliveries();
    } catch (e) {
      console.error(e);
      toast.error(
        "Re-export failed",
        e?.response?.data?.error || "Unable to trigger re-export",
      );
    } finally {
      setReExportingDeliveryId(null);
    }
  };

  // The Exports tab badge must show a blocked or failed connector without the
  // tab being opened (its panel only mounts when it is), so count them here.
  useEffect(() => {
    if (!features.connectors || !request?.template?.id) return;
    requestConnectorsApi
      .list(id)
      .then((res) =>
        setFailedConnectorCount(
          (res.data?.connectors || []).filter((c) => c.latest_delivery?.status === "failed").length,
        ),
      )
      .catch(() => {});
  }, [features.connectors, id, request?.template?.id, request?.status]);

  useEffect(() => {
    fetchRequest();
    fetchAuditLogs(); // Fetch audit logs on mount
    // Fetch batch audit summary for all fields (single API call instead of N)
    fetchFieldsAuditSummary();
    fetchOutputDeliveries(); // Fetch output destination export history on mount
  }, [id]);

  // Polling effect for processing status
  useEffect(() => {
    // Check if request is in processing status
    const isProcessing =
      request?.status === "processing" || request?.status === "pending";
    setShowProcessingModal(isProcessing);

    if (isProcessing) {
      // Track when processing started (use request updated_at or current time)
      if (!processingStartTimeRef.current) {
        const startTime = request?.updated_at
          ? new Date(request.updated_at)
          : new Date();
        processingStartTimeRef.current = startTime;
      }

      setShowProcessingLog(true); // Auto-open the log panel
      // Start silent polling every 5 seconds during processing (for live field count updates)
      pollingIntervalRef.current = setInterval(() => {
        silentPollRequest();
      }, 5000);

      // Also poll the processing log every 3 seconds for real-time step updates
      const activeJobId = request?.active_job?.id;
      if (activeJobId) {
        processingJobIdRef.current = activeJobId;
        // Initial fetch immediately
        jobsApi
          .getProcessingLog(activeJobId)
          .then((res) => {
            const data = res.data;
            setProcessingLog(data.processing_log || []);
            setProcessingProgress({
              percent: data.progress_percent || 0,
              message: data.progress_message || "Starting...",
            });
          })
          .catch(() => {});

        processingLogPollRef.current = setInterval(async () => {
          try {
            const res = await jobsApi.getProcessingLog(activeJobId);
            const data = res.data;
            setProcessingLog(data.processing_log || []);
            setProcessingProgress({
              percent: data.progress_percent || 0,
              message: data.progress_message || "Processing...",
            });
          } catch (err) {
            // Silently ignore — job may have completed
          }
        }, 3000);
      }

      // Start stuck timer — update elapsed minutes every 30 seconds
      stuckTimerRef.current = setInterval(() => {
        if (processingStartTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - processingStartTimeRef.current.getTime()) / 60000,
          );
          setStuckMinutes(elapsed);
        }
      }, 30000);
      // Also compute immediately
      if (processingStartTimeRef.current) {
        const elapsed = Math.floor(
          (Date.now() - processingStartTimeRef.current.getTime()) / 60000,
        );
        setStuckMinutes(elapsed);
      }
    } else {
      // Clear polling when not processing
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (processingLogPollRef.current) {
        clearInterval(processingLogPollRef.current);
        processingLogPollRef.current = null;
      }
      // Clear stuck timer
      if (stuckTimerRef.current) {
        clearInterval(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
      processingStartTimeRef.current = null;
      setStuckMinutes(0);
    }

    // Cleanup on unmount or status change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (processingLogPollRef.current) {
        clearInterval(processingLogPollRef.current);
      }
      if (stuckTimerRef.current) {
        clearInterval(stuckTimerRef.current);
      }
    };
  }, [request?.status, request?.active_job?.id]);

  // Document processing banner: detect when request is in reviewing/extracted status but still has an active job
  // Uses lightweight job status polling to avoid resetting user edits
  useEffect(() => {
    const activeJob = request?.active_job;
    const isReviewingButStillProcessing =
      activeJob &&
      (activeJob.status === "pending" || activeJob.status === "running") &&
      (request?.status === "reviewing" || request?.status === "extracted");

    if (isReviewingButStillProcessing) {
      setDocumentsStillProcessing(true);
      setProcessingComplete(false);
      setShowProcessingLog(true); // Auto-open log panel
      processingJobIdRef.current = activeJob.id;
      setProcessingProgress({
        percent: activeJob.progress_percent || 0,
        message:
          activeJob.progress_message || "Processing remaining documents...",
      });

      // Poll every 10 seconds using lightweight job status endpoint (no data reset)
      if (!processingBannerPollRef.current && activeJob.id) {
        processingBannerCompletionHandledRef.current = false;
        processingBannerPollRef.current = setInterval(async () => {
          try {
            // Use processing-log endpoint to get both status AND step logs
            const res = await jobsApi.getProcessingLog(
              processingJobIdRef.current,
            );
            const data = res.data;
            if (data.processing_log) {
              setProcessingLog(data.processing_log);
            }
            if (data.status === "completed" || data.status === "failed") {
              // Job finished — refresh the data right away instead of making
              // the user click a "Refresh Now" button, and surface a toast
              // as confirmation rather than a persistent inline banner.
              // processingComplete stays true (keeping Publish/Save disabled
              // via backgroundJobActive) only until the refetch lands.
              //
              // The 5s interval doesn't wait for a tick's own request to
              // finish before scheduling the next one, so a slow response
              // can leave two ticks in flight together; both would see
              // "completed" and re-enter this block. Guard with a ref so
              // only the tick that resumes first actually acts on it.
              if (processingBannerCompletionHandledRef.current) return;
              processingBannerCompletionHandledRef.current = true;
              clearInterval(processingBannerPollRef.current);
              processingBannerPollRef.current = null;
              setDocumentsStillProcessing(false);
              setProcessingComplete(true);
              if (hasUnsavedChangesRef.current) {
                // Refetching now would replace editedFields and silently
                // discard the user's edits. Keep processingComplete set (so
                // actions stay disabled) and let them refresh explicitly.
                setRefreshHeldForEdits(true);
                return;
              }
              setProcessingProgress({
                percent: 100,
                message:
                  data.status === "completed"
                    ? "All documents processed successfully!"
                    : `Processing finished with errors: ${data.error_message || "Unknown error"}`,
              });
              Promise.all([
                fetchRequest(),
                fetchAuditLogs(),
                fetchFieldsAuditSummary(),
              ]).finally(() => setProcessingComplete(false));
              if (data.status === "completed") {
                toast.success(
                  "All documents have been processed",
                  "New extracted fields loaded automatically.",
                );
              } else {
                toast.error(
                  "Processing finished with errors",
                  data.error_message || "Unknown error",
                );
              }
            } else {
              // Still running — update progress
              setProcessingProgress({
                percent: data.progress_percent || 0,
                message:
                  data.progress_message || "Processing remaining documents...",
              });
            }
          } catch (err) {
            console.error("Error polling job status:", err);
          }
        }, 5000);
      }
    }

    return () => {
      if (processingBannerPollRef.current) {
        clearInterval(processingBannerPollRef.current);
        processingBannerPollRef.current = null;
      }
    };
  }, [request?.active_job?.id, request?.status]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  });

  // Lets the OpCo WorkspaceSwitcher confirm before a reload drops edits.
  useEffect(() => {
    window.__xtractDirty = hasUnsavedChanges();
    return () => { window.__xtractDirty = false; };
  });

  // Ctrl+S / Cmd+S keyboard shortcut to save (Save Changes only, never Publish)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (saving || publishing) return;

        if (isRequestLocked(request?.status)) {
          // Terminal (approved/rejected/failed/completed/cancelled): the
          // backend rejects the save, so the shortcut does nothing.
          return;
        }

        if (request?.status === "published") {
          if (hasUnsavedChanges()) {
            openSaveModeSelector({ requireReason: true });
          }
        } else if (!isViewOnly && hasUnsavedChanges()) {
          openSaveModeSelector({ requireReason: false });
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // Field search filter state
  const [fieldSearchQuery, setFieldSearchQuery] = useState("");
  const [fieldsViewFilter, setFieldsViewFilter] = useState("all"); // 'all', 'mandatory' or 'custom'
  const [fieldsExtractionFilter, setFieldsExtractionFilter] = useState("all"); // 'all', 'extracted', 'not_extracted'
  const fieldsViewTabRefs = useRef({});
  const [fieldsViewIndicatorStyle, setFieldsViewIndicatorStyle] = useState({
    left: 0,
    width: 0,
  });

  useLayoutEffect(() => {
    const activeEl = fieldsViewTabRefs.current[fieldsViewFilter];
    if (activeEl) {
      setFieldsViewIndicatorStyle({
        left: activeEl.offsetLeft,
        width: activeEl.offsetWidth,
      });
    }
  }, [fieldsViewFilter, leftPanelTab]);

  const fetchLiveTemplateAiLearningEnabled = async () => {
    try {
      const res = await requestsApi.get(id);
      const enabled = !!res.data?.template?.ai_learning_enabled;
      setLiveTemplateAiLearningEnabled(enabled);
      return enabled;
    } catch (err) {
      console.warn("Failed to refresh template AI toggle from DB", err);
      setLiveTemplateAiLearningEnabled(false);
      return false;
    }
  };

  const persistChanges = async ({
    reason,
    applyAiLearning,
    aiLearningRules = null,
  }) => {
    try {
      setSaving(true);
      const changedFields = buildChangedFieldsPayload();
      const metadataChanged =
        JSON.stringify(editedMetadata) !== JSON.stringify(originalMetadata);

      if (metadataChanged) {
        await requestsApi.update(id, {
          title: editedMetadata.title,
          description: editedMetadata.description,
          issuer: editedMetadata.issuer,
        });
      }

      let saveRes = null;
      if (changedFields.length > 0) {
        const saveOptions = applyAiLearning
          ? {
              save_mode: "save_with_ai_learning",
              ...(Array.isArray(aiLearningRules) && aiLearningRules.length > 0
                ? { ai_learning_rules: aiLearningRules }
                : {}),
            }
          : {};

        saveRes = await requestsApi.updateFields(
          id,
          changedFields,
          reason,
          saveOptions,
        );
      }

      setOriginalFields(JSON.parse(JSON.stringify(editedFields)));
      setOriginalMetadata(JSON.parse(JSON.stringify(editedMetadata)));

      const savedLearning = saveRes?.data?.ai_learning_rules_saved || 0;
      const learningWarnings = saveRes?.data?.ai_learning_warnings || [];
      if (applyAiLearning) {
        if (savedLearning > 0) {
          console.log("[AI_LEARNING] triggered", {
            requestId: id,
            rulesSaved: savedLearning,
          });
          setAiLearningDebugStatus(
            `AI learning: triggered (${savedLearning} rule${savedLearning !== 1 ? "s" : ""} saved)`,
          );
        } else {
          const warningText =
            learningWarnings[0] || "no eligible learning update";
          console.log("[AI_LEARNING] skipped", {
            requestId: id,
            reason: warningText,
          });
          setAiLearningDebugStatus(`AI learning: skipped (${warningText})`);
        }
      } else if (changedFields.length > 0 || metadataChanged) {
        console.log("[AI_LEARNING] skipped", {
          requestId: id,
          reason: "Save in DB selected",
        });
        setAiLearningDebugStatus("AI learning: skipped (Save in DB selected)");
      }

      if (savedLearning > 0) {
        toast.success(
          "Changes saved successfully",
          `${savedLearning} AI learning rule${savedLearning !== 1 ? "s" : ""} saved.`,
        );
      } else if (changedFields.length > 0 || metadataChanged) {
        toast.success("Changes saved successfully");
      } else {
        toast.info("No changes to save");
      }
      if (learningWarnings.length > 0) {
        toast.warning("AI learning note", learningWarnings[0]);
      }

      fetchRequest();
      fetchAuditLogs();
      return true;
    } catch (err) {
      console.error("Error saving fields:", err);
      // A failed save is recoverable — the user is still looking at their
      // unsaved edits and should be able to fix the reason/value and retry.
      // Routing it through the page-level `error` state (as this used to)
      // replaced the whole request view with a bare "Back" banner, so a
      // single failed save looked like the request itself had broken.
      toast.error(err.response?.data?.error || "Failed to save changes");
      if (applyAiLearning) {
        setAiLearningDebugStatus("AI learning: failed (save error)");
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openSaveModeSelector = async ({
    requireReason,
    postSaveAction = null,
    defaultReason = "User saved changes",
  } = {}) => {
    if (!hasUnsavedChanges()) {
      toast.info("No changes to save");
      return;
    }
    // Open modal first for a snappy UX, then refresh DB-backed toggle state.
    setSaveFlowContext({
      requireReason: !!requireReason,
      postSaveAction,
      defaultReason,
    });
    setShowSaveModeModal(true);

    setLoadingSaveModeOptions(true);
    try {
      const enabled = await fetchLiveTemplateAiLearningEnabled();
      setLiveTemplateAiLearningEnabled(enabled);
    } finally {
      setLoadingSaveModeOptions(false);
    }
  };

  const loadAiLearningSuggestionsForField = async (fieldOption) => {
    if (!fieldOption?.id || !fieldOption?.field_value) {
      setAiAssistSuggestions([]);
      return;
    }
    try {
      setAiAssistLoading(true);
      setAiAssistSelectedRuleContent("");
      const res = await requestsApi.getAiLearningSuggestions(id, {
        field_id: fieldOption.id,
        corrected_value: fieldOption.field_value,
      });
      setAiAssistSuggestions(res.data?.suggestions || []);
    } catch (err) {
      console.error("Failed to load AI suggestions:", err);
      setAiAssistSuggestions([]);
      toast.warning(
        "AI suggestions unavailable",
        "You can still continue by entering a custom instruction.",
      );
    } finally {
      setAiAssistLoading(false);
    }
  };

  const openAiLearningAssistModal = async () => {
    if (aiLearningCandidateCount !== 1) {
      toast.warning(
        "Only single-field learning is allowed",
        aiLearningCandidateCount === 0
          ? "Edit exactly one field with a value to apply AI learning."
          : "You edited more than one field. Keep exactly one edited field to apply AI learning.",
      );
      return;
    }

    const selectedCandidate = aiLearningCandidates[0];

    setAiAssistFieldOptions([selectedCandidate]);
    setAiAssistSelectedFieldId(selectedCandidate.id);
    setAiAssistSuggestions([]);
    setAiAssistSelectedRuleContent("");
    setAiAssistCustomInstruction("");
    setShowAiLearningAssistModal(true);
    await loadAiLearningSuggestionsForField(selectedCandidate);
  };

  // Runs the action (publish / send for review) that was deferred so the
  // save-mode selector / AI-learning modal could be shown first.
  const runPostSaveAction = async (postSaveAction) => {
    if (postSaveAction === "publish") {
      await finalizePublish();
    } else if (postSaveAction === "sendForReview") {
      await finalizeSendForReview();
    }
  };

  const handleChooseSaveInDb = async () => {
    setShowSaveModeModal(false);
    setSaveIntent("db");

    if (saveFlowContext.requireReason) {
      setShowSaveReasonModal(true);
      return;
    }

    const ok = await persistChanges({
      reason: saveFlowContext.defaultReason || "User saved changes",
      applyAiLearning: false,
    });
    if (ok && saveFlowContext.postSaveAction) {
      await runPostSaveAction(saveFlowContext.postSaveAction);
    }
  };

  const handleChooseSaveInDbAndAi = async () => {
    if (!currentTemplateAiLearningEnabled) {
      toast.warning(
        "Template AI learning is off",
        "Enable the template AI learning toggle first to use this option.",
      );
      return;
    }
    if (aiLearningCandidateCount !== 1) {
      toast.warning(
        "Only single-field learning is allowed",
        aiLearningCandidateCount === 0
          ? "Edit exactly one field with a value to apply AI learning."
          : "You edited more than one field. Keep exactly one edited field to apply AI learning.",
      );
      return;
    }
    setShowSaveModeModal(false);
    setSaveIntent("db_ai");
    setPendingAiSaveReason("AI learning applied - no user reason");
    setShowSaveReasonModal(false);
    setSaveReason("");
    await openAiLearningAssistModal();
  };

  // Handler for saving with reason
  const handleSaveWithReason = async () => {
    if (!saveReason.trim()) return;

    if (saveIntent === "db_ai") {
      setPendingAiSaveReason(saveReason.trim());
      setShowSaveReasonModal(false);
      setSaveReason("");
      await openAiLearningAssistModal();
      return;
    }

    try {
      const ok = await persistChanges({
        reason: saveReason,
        applyAiLearning: false,
      });
      setShowSaveReasonModal(false);
      setSaveReason("");
      if (ok && saveFlowContext.postSaveAction) {
        await runPostSaveAction(saveFlowContext.postSaveAction);
      }
    } catch (err) {
      console.error("Error saving fields:", err);
    }
  };

  const handleApplyAiLearningSave = async () => {
    const customInstruction = (aiAssistCustomInstruction || "").trim();
    const suggestionInstruction = (aiAssistSelectedRuleContent || "").trim();
    const hasCustomInstruction = customInstruction.length > 0;
    const hasSelectedSuggestion = suggestionInstruction.length > 0;
    const hasExactlyOneInstructionSource =
      Number(hasCustomInstruction) + Number(hasSelectedSuggestion) === 1;

    if (!hasExactlyOneInstructionSource) {
      toast.warning(
        "Choose one instruction source",
        "Select one AI suggestion chip or enter a custom instruction (only one is allowed).",
      );
      return;
    }

    const selectedField = aiAssistFieldOptions.find(
      (f) => String(f.id) === String(aiAssistSelectedFieldId),
    );
    if (!selectedField?.template_field_id || !selectedField?.field_value) {
      toast.warning(
        "Select a field",
        "Choose one changed field before confirming AI learning.",
      );
      return;
    }

    // Custom input takes precedence. Suggestion is used only if custom is empty.
    let finalInstruction = "";
    if (hasCustomInstruction) {
      finalInstruction = customInstruction;
    } else if (hasSelectedSuggestion) {
      finalInstruction = suggestionInstruction;
    }

    try {
      const aiLearningRules = finalInstruction
        ? [
            {
              template_field_id: selectedField.template_field_id,
              instruction: finalInstruction,
              corrected_value: selectedField.field_value,
              rule_type: "custom_instruction",
            },
          ]
        : [];

      const ok = await persistChanges({
        reason: pendingAiSaveReason || "User saved changes",
        applyAiLearning: true,
        aiLearningRules,
      });
      if (!ok) return;

      setAiAssistFieldOptions([]);
      setAiAssistSelectedFieldId(null);
      setAiAssistSuggestions([]);
      setAiAssistCustomInstruction("");
      setAiAssistSelectedRuleContent("");
      setPendingAiSaveReason("");
      setShowAiLearningAssistModal(false);

      if (saveFlowContext.postSaveAction) {
        await runPostSaveAction(saveFlowContext.postSaveAction);
      }
    } catch (err) {
      console.error("Failed during AI-learning assisted save:", err);
      toast.error("Failed to save with AI learning");
    }
  };

  const handleSaveChanges = () => {
    openSaveModeSelector({ requireReason: false });
  };

  // Handler for discarding changes — restores the values last loaded from
  // (or saved to) the server.
  const handleDiscardChanges = () => {
    setEditedFields(JSON.parse(JSON.stringify(originalFields)));
    setEditedMetadata(JSON.parse(JSON.stringify(originalMetadata)));
  };

  const handleFieldChange = (fieldId, newValue) => {
    setEditedFields((prev) =>
      prev.map((f) => (f.id === fieldId ? { ...f, field_value: newValue } : f)),
    );
  };

  // Manual refresh after a background job finished while edits were pending.
  // Discards those edits — the banner says so.
  const handleProcessingRefresh = () => {
    setRefreshHeldForEdits(false);
    Promise.all([
      fetchRequest(),
      fetchAuditLogs(),
      fetchFieldsAuditSummary(),
    ]).finally(() => setProcessingComplete(false));
  };

  // Check if fields or metadata have been modified
  const hasUnsavedChanges = () => {
    const fieldsChanged = JSON.stringify(editedFields) !== JSON.stringify(originalFields)
    const metadataChanged = JSON.stringify(editedMetadata) !== JSON.stringify(originalMetadata)
    return fieldsChanged || metadataChanged
  }

  // Kept fresh every render (no dependency array) so the background-processing
  // poll below — whose interval closure is set up once per active_job.id and
  // can outlive several renders — always sees the user's LATEST edits rather
  // than whatever was in scope when that interval was created. Reading
  // hasUnsavedChanges() directly from inside that stale closure would risk
  // auto-refreshing (and silently discarding) edits made after the poll started.
  const hasUnsavedChangesRef = useRef(false)
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges()
  })

  const handleExtract = async () => {
    try {
      setPublishing(true)
      await requestsApi.analyze(id)
      toast.success('Extraction started')
      setTimeout(() => fetchRequest(), 1000)
    } catch (err) {
      console.error('Error starting extraction:', err)
      toast.error('Failed to start extraction')
    } finally {
      setPublishing(false)
    }
  }

  // Approves/publishes the request. Assumes any field/metadata edits have
  // already been persisted (either there were none, or the save-mode
  // selector / AI-learning flow just completed one).
  const finalizePublish = async () => {
    try {
      setPublishing(true);
      const res = await requestsApi.approve(id);
      // Approval can succeed while a connector refuses the request (e.g. its
      // Map Payload no longer matches the template) — say so rather than
      // reporting a plain success.
      const blocked = res?.data?.publish_blocked || [];
      if (blocked.length > 0) {
        const names = blocked.map((b) => b.name || `Connector ${b.webhook_id}`).join(", ");
        toast.warning(
          `Approved, but not sent to ${names}`,
          `${plainReason(blocked[0].reason)}${blocked.length > 1 ? ` (+${blocked.length - 1} more)` : ""} See the Exports tab.`,
        );
        setFailedConnectorCount(blocked.length);
      } else {
        toast.success("Request published successfully");
      }
      fetchRequest();
      fetchAuditLogs(); // Refresh audit logs after publish

      // Output destination export(s) fire on a background thread after
      // approve returns, so an immediate fetchOutputDeliveries() would
      // likely miss them. Re-fetch once after a short delay; if any
      // delivery comes back "pending", the polling effect above takes
      // over and keeps refreshing until it resolves (or the attempt cap
      // is hit).
      if (outputDeliveriesPublishRefetchRef.current) {
        clearTimeout(outputDeliveriesPublishRefetchRef.current);
      }
      outputDeliveriesPublishRefetchRef.current = setTimeout(() => {
        fetchOutputDeliveries();
        outputDeliveriesPublishRefetchRef.current = null;
      }, 2500);
    } catch (err) {
      console.error("Error publishing:", err);
      toast.error("Failed to publish request");
      setError("Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  // Moves the request into review. Same assumption as finalizePublish.
  const finalizeSendForReview = async () => {
    try {
      setSendingForReview(true);
      await requestsApi.sendForReview(id);
      toast.success("Request sent for review");
      fetchRequest();
      fetchAuditLogs();
    } catch (err) {
      console.error("Error sending for review:", err);
      toast.error("Failed to send for review");
    } finally {
      setSendingForReview(false);
    }
  };

  const handlePublish = async () => {
    // Route unsaved edits through the same save-mode selector / AI-learning
    // flow as "Save Changes" so the AI Learning pop-up isn't skipped just
    // because the user clicked Publish instead (see #440).
    if (hasUnsavedChanges()) {
      await openSaveModeSelector({
        requireReason: false,
        postSaveAction: "publish",
        defaultReason: "User published changes",
      });
      return;
    }
    await finalizePublish();
  };

  const handleSendForReview = async () => {
    if (hasUnsavedChanges()) {
      await openSaveModeSelector({
        requireReason: false,
        postSaveAction: "sendForReview",
        defaultReason: "Saved before sending for review",
      });
      return;
    }
    await finalizeSendForReview();
  };

  const handleAlternativeSelect = async (altFieldId, fieldName, altData) => {
    try {
      await requestsApi.setFieldActive(id, altFieldId);

      // Update only the affected field locally — preserves other fields' unsaved edits
      const updateField = (f) => {
        if (f.field_name === fieldName) {
          return {
            ...f,
            id: altData.id,
            field_value: altData.field_value || altData.extracted_value || "",
            extracted_value:
              altData.extracted_value || altData.field_value || "",
            confidence: altData.confidence,
            source_id: altData.source_document_id,
            source_document_id: altData.source_document_id,
            source_document_filename: altData.source_document_filename,
            page_number: altData.page_number,
            bounding_box: altData.bounding_box,
          };
        }
        return f;
      };

      setEditedFields((prev) => prev.map(updateField));
      // Also update originalFields so hasUnsavedChanges() stays accurate
      // (alternative selection is persisted server-side immediately)
      setOriginalFields((prev) => prev.map(updateField));

      fetchAuditLogs(); // Refresh audit logs after alternative selection
      fetchFieldsAuditSummary(); // Refresh batch audit summary
    } catch (err) {
      console.error("Error selecting alternative:", err);
      setError("Failed to select alternative value");
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) return;
    setCancelError("");
    setCancelling(true);
    try {
      await requestsApi.cancel(id, cancelReason);
      setShowCancelReasonModal(false);
      setCancelReason("");
      setCancelError("");
      navigate(-1);
    } catch (err) {
      console.error("Error cancelling:", err);
      setCancelError(
        err?.response?.data?.error ||
          "Failed to cancel request. Please try again.",
      );
    } finally {
      setCancelling(false);
    }
  };

  // Auto-cancel stuck request from processing modal
  const handleStuckCancel = async () => {
    setStuckCancelling(true);
    try {
      const reason = `Request stuck in processing for ${stuckMinutes} minute${stuckMinutes !== 1 ? "s" : ""} — auto-cancelled`;
      await requestsApi.cancel(id, reason);
      setShowProcessingModal(false);
      navigate(-1);
    } catch (err) {
      console.error("Error cancelling stuck request:", err);
      toast.error("Failed to cancel stuck request. Please try again.");
    } finally {
      setStuckCancelling(false);
    }
  };

  // Handle document upload to request
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the same file can be re-selected
    e.target.value = "";
    try {
      setUploading(true);
      const res = await requestsApi.uploadDocument(id, file);
      const newDocId = res.data?.id;
      toast.success(`"${file.name}" uploaded successfully`);
      // Refresh request to include the new document in tabs
      const refreshed = await requestsApi.get(id);
      const updatedRequest = refreshed.data;
      setRequest(updatedRequest);
      setEditedFields(updatedRequest.fields || []);
      // Switch to the newly uploaded document tab
      const docs = updatedRequest.documents || [];
      const newIdx = newDocId
        ? docs.findIndex((d) => d.id === newDocId)
        : docs.length - 1;
      if (newIdx >= 0) {
        setActiveDocTab(newIdx);
        setDocumentId(docs[newIdx].id);
        // Auto-scroll tab bar to make the new tab visible
        setTimeout(() => {
          const tabEl = docTabsRef.current?.querySelector(
            `[data-doc-tab="${newIdx}"]`,
          );
          if (tabEl)
            tabEl.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "nearest",
            });
        }, 100);
      }
    } catch (err) {
      console.error("Error uploading document:", err);
      toast.error(err?.response?.data?.error || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  // Handle field click - switch to correct document and highlight field
  const handleFieldClick = (field) => {
    const documents = request?.documents || [];

    // Only highlight if field has an actual extracted value
    const hasExtractedValue =
      (field.field_value && field.field_value.trim() !== "") ||
      (field.extracted_value && field.extracted_value.trim() !== "");

    // If field has source_id (document ID) and has a value, switch to that document
    if (hasExtractedValue && field.source_id && documents.length > 0) {
      const docIndex = documents.findIndex((doc) => doc.id === field.source_id);
      if (docIndex !== -1) {
        setActiveDocTab(docIndex);
        setDocumentId(documents[docIndex].id);
      }
    }

    // Only set the highlighted field if there's a value to highlight
    // (PDFViewer auto-scrolls to the field's page when highlightedField changes)
    if (hasExtractedValue) {
      setHighlightedField(field);
    } else {
      // Clear any previous highlight for empty fields
      setHighlightedField(null);
    }
  };

  // Scroll to & briefly pulse-highlight a required-but-empty field. Cycles through
  // the list of empty required fields one at a time on repeated clicks.
  const scrollToEmptyRequiredField = (fieldsList) => {
    if (!fieldsList || fieldsList.length === 0) return;
    const idx = emptyFieldCycleIndex % fieldsList.length;
    const field = fieldsList[idx];
    setEmptyFieldCycleIndex(idx + 1);

    // Ensure the field row is actually visible: clear any search/filter that
    // might otherwise hide it, then scroll to it on the next tick.
    setFieldSearchQuery("");
    setFieldsViewFilter("all");

    setTimeout(() => {
      const el = fieldRowRefs.current[String(field.id)];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setPulsedFieldId(String(field.id));
        setTimeout(() => {
          setPulsedFieldId((current) =>
            current === String(field.id) ? null : current,
          );
        }, 1800);
      }
    }, 50);
  };

  // Handle viewing a field value in the PDF viewer
  const handleViewInDocument = (docId, pageNumber, boundingBox) => {
    const documents = request?.documents || [];

    // Switch to the correct document
    if (docId && documents.length > 0) {
      const docIndex = documents.findIndex((doc) => doc.id === docId);
      if (docIndex !== -1) {
        setActiveDocTab(docIndex);
        setDocumentId(documents[docIndex].id);
      }
    }

    // Set page and highlight
    if (pageNumber) {
      setCurrentPage(pageNumber);
    }

    // Set highlight info for bounding box
    if (boundingBox) {
      setHighlightedField({
        bounding_box: boundingBox,
        page_number: pageNumber,
      });
    }
  };

  const handleSearch = () => {
    if (!searchText.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchResults([
      { page: 1, count: 3 },
      { page: 5, count: 1 },
    ]);
  };

  // Calculate average confidence: sum of all extracted field confidences / number of extracted fields, as 0-100
  const extractedFields = editedFields.filter(
    (f) => f.confidence != null && f.confidence > 0,
  );
  const hasConfidenceData = extractedFields.length > 0;
  const avgConfidence = hasConfidenceData
    ? Math.min(
        100,
        Math.max(
          0,
          Math.round(
            (extractedFields.reduce(
              (sum, f) => sum + (f.confidence || 0),
              0,
            ) /
              extractedFields.length) *
              100,
          ),
        ),
      )
    : 0;

  // Group fields dynamically based on template or fallback to static categories
  const getFieldsByCategory = () => {
    // ALWAYS use stored extraction fields as the primary source.
    // Template fields may have changed since extraction — stored data must never disappear.
    if (!editedFields || editedFields.length === 0) {
      // Extraction hasn't produced any field rows yet. Rather than leaving the
      // panel blank while the job runs, show the template's known fields
      // immediately as empty placeholders — same shape/grouping real
      // extracted fields end up in — so the layout is stable and fields
      // simply fill in as silentPollRequest() pulls in results. Once the
      // job leaves pending/processing, editedFields is authoritative again.
      if (!showProcessingModal || !request?.template?.fields_by_category) {
        return {};
      }
      const placeholders = {};
      Object.entries(request.template.fields_by_category).forEach(
        ([category, tFields]) => {
          placeholders[category] = tFields
            .map((tf) => ({
              id: `placeholder-${tf.id}`,
              template_field_id: tf.id,
              field_name: tf.field_name,
              display_name:
                tf.display_name ||
                tf.field_name
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (s) => s.toUpperCase())
                  .trim(),
              field_value: "",
              extracted_value: "",
              confidence: null,
              source_type: null,
              field_type: tf.field_type || "text",
              field_values: tf.field_values || null,
              is_required: tf.extraction_is_required || false,
              sort_order: tf.sort_order ?? 999,
              ui_properties: tf.ui_properties || null,
              validation_rules: tf.validation_rules || null,
              _isPlaceholder: true,
            }))
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        },
      );
      return placeholders;
    }

    // Build a lookup from current template fields for supplemental metadata only
    const templateFieldMap = {};
    if (request?.template?.fields_by_category) {
      Object.entries(request.template.fields_by_category).forEach(
        ([category, tFields]) => {
          tFields.forEach((tf) => {
            templateFieldMap[tf.field_name] = { ...tf, _category: category };
          });
        },
      );
    }

    const result = {};
    editedFields.forEach((f) => {
      // Email-extracted fields go into their own category for clear segregation
      const isEmailField = f.source_type === "email_body";

      // Determine category: email fields → 'email_extracted',
      // otherwise prefer template snapshot, then current template, then 'other'
      const category = isEmailField
        ? "email_extracted"
        : f.template_field?.category ||
          templateFieldMap[f.field_name]?._category ||
          "other";

      // Determine display_name: prefer stored template_field, then current template, then derive from field_name
      const tf = templateFieldMap[f.field_name];
      const display_name =
        f.template_field?.display_name ||
        tf?.display_name ||
        f.field_name
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (s) => s.toUpperCase())
          .trim();

      if (!result[category]) result[category] = [];
      result[category].push({
        ...f,
        display_name,
        field_type: f.template_field?.field_type || tf?.field_type || "text",
        field_values:
          f.template_field?.field_values || tf?.field_values || null,
        is_required:
          f.is_required ||
          f.template_field?.extraction_is_required ||
          tf?.extraction_is_required ||
          false,
        sort_order: f.template_field?.sort_order ?? tf?.sort_order ?? 999,
        ui_properties: tf?.ui_properties || null,
        validation_rules:
          f.template_field?.validation_rules || tf?.validation_rules || null,
      });
    });

    // Sort fields within each category by sort_order
    Object.values(result).forEach((arr) =>
      arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    );

    return result;
  };

  const fieldsByCategory = getFieldsByCategory();

  // Compute required fields that are still empty
  const allGroupedFields = Object.values(fieldsByCategory).flat();
  const requiredFields = allGroupedFields.filter((f) => f.is_required);
  const emptyRequiredFields = requiredFields.filter((f) => {
    const val = f.field_value ?? f.extracted_value ?? "";
    if (Array.isArray(val)) return val.length === 0;
    return val === null || val === undefined || String(val).trim() === "";
  });
  const hasEmptyRequiredFields = emptyRequiredFields.length > 0;

  // Get category order from template (defines the order categories appear in UI)
  const categoryOrder = request?.template?.category_order || [];

  // Build category metadata map from category_order (includes display names, icons, colors)
  const categoryMetadata = categoryOrder.reduce((acc, cat) => {
    acc[cat.name] = {
      displayName: cat.display_name,
      description: cat.description,
      icon: cat.icon,
      color: cat.color,
      isVisible: cat.is_visible,
    };
    return acc;
  }, {});

  // Get category display names from template metadata or use fallback defaults
  const getCategoryDisplayName = (categoryName) => {
    if (categoryName === "email_extracted") return "📧 Email Extracted Fields";
    return (
      categoryMetadata[categoryName]?.displayName ||
      request?.template?.category_display_names?.[categoryName] ||
      categoryName.charAt(0).toUpperCase() +
        categoryName.slice(1).replace(/_/g, " ")
    );
  };

  // Get category color for styling
  const getCategoryColor = (categoryName) => {
    if (categoryName === "email_extracted") return "#6366f1"; // Indigo for email fields
    return categoryMetadata[categoryName]?.color || "gray";
  };

  // Get ordered categories based on template category_order, or fallback to Object.keys
  const getOrderedCategories = () => {
    const allCats = Object.keys(fieldsByCategory).filter(
      (cat) => fieldsByCategory[cat]?.length > 0,
    );

    let ordered;
    if (categoryOrder.length > 0) {
      // Use the order defined in the database, filter by visibility and available fields
      ordered = categoryOrder
        .filter((co) => co.is_visible !== false)
        .map((co) => co.name)
        .filter(
          (name) => fieldsByCategory[name] && fieldsByCategory[name].length > 0,
        );
      // Append any categories from stored fields that aren't in the current template order
      const orderedSet = new Set(ordered);
      allCats.forEach((cat) => {
        if (!orderedSet.has(cat)) ordered.push(cat);
      });
    } else {
      ordered = [...allCats];
    }

    // Email-extracted fields always appear last for clear visual segregation
    if (ordered.includes("email_extracted")) {
      ordered = ordered.filter((c) => c !== "email_extracted");
      ordered.push("email_extracted");
    }

    return ordered;
  };

  const orderedCategories = getOrderedCategories();

  // Get document icon based on file type
  const getDocIcon = (filename) => {
    if (filename?.endsWith(".pdf")) return FileText;
    if (filename?.endsWith(".xlsx") || filename?.endsWith(".xls"))
      return FileSpreadsheet;
    return File;
  };

  // Check if a document is a PDF (viewable in PDF viewer)
  const isPdfDocument = (doc) => {
    if (!doc) return false;
    if (doc.content_type === "application/pdf") return true;
    if (doc.filename?.toLowerCase().endsWith(".pdf")) return true;
    return false;
  };

  // Get human-readable file type label
  const getFileTypeLabel = (filename) => {
    if (!filename) return "Document";
    const ext = filename.split(".").pop()?.toLowerCase();
    const labels = {
      xlsx: "Excel Spreadsheet",
      xls: "Excel Spreadsheet",
      doc: "Word Document",
      docx: "Word Document",
      csv: "CSV File",
      txt: "Text File",
      msg: "Email Message",
      eml: "Email Message",
      png: "Image",
      jpg: "Image",
      jpeg: "Image",
      gif: "Image",
      bmp: "Image",
    };
    return labels[ext] || `${(ext || "").toUpperCase()} File`;
  };

  if (loading) {
    return <RequestDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      </div>
    );
  }

  if (!request) return null;

  // An NS-enabled request must not be rendered until the feature flags have
  // actually arrived. features.netsuite starts false and only flips once
  // /api/auth/features resolves, so deciding the view before then renders the
  // generic extraction screen for what is really a NetSuite request — no
  // Supplier/Bid Line Items, no way to post. Keep showing the loading state
  // for the moment it takes rather than committing to the wrong screen.
  if (request?.template_ns_enabled && !features.loaded) {
    return <RequestDetailSkeleton />;
  }

  // NS templates use a review screen chosen by the template's NetSuite document
  // type, but only while NetSuite is enabled org-wide. Vendor Bid edits two
  // linked custom records with an entirely different field set from a Purchase
  // Order — routing both through NSRequestView rendered Bid requests with PO's
  // Location/Shipping Terms/Currency fields against Bid's validation rules,
  // producing errors the screen offered no way to clear.
  if (request?.template_ns_enabled && features.netsuite) {
    // Canonicalised: a template saved with the Special Pricing record ids via
    // the generic picker is a Vendor Bid and gets the Bid screen.
    const nsEntityType = canonicalNsEntityType(request?.template_ns_entity_type) || "purchase_order";
    // A template's document type routes to its own review screen — PO and
    // Vendor Bid keep their existing, hand-built screens; any other
    // (admin-configured) record type gets the generic one, driven entirely
    // by that record type's own configured fields rather than another
    // binary PO-vs-Bid check here.
    const NsView =
      nsEntityType === "vendor_bid" ? NSBidRequestView :
      nsEntityType === "purchase_order" ? NSRequestView :
      NSGenericRequestView;
    return (
      <NsView
        request={request}
        showProcessingModal={showProcessingModal}
        processingMinimized={processingMinimized}
        setProcessingMinimized={setProcessingMinimized}
        processingProgress={processingProgress}
        processingLog={processingLog}
        onExtract={fetchRequest}
        onOpCoMoved={fetchRequest}
      />
    );
  }

  // BC templates use BCRequestView — even while processing — but only while
  // Business Central is enabled org-wide. Pass processing state so BCRequestView
  // renders the extraction overlay on top of the BC layout.
  if (request?.template_bc_enabled && features.business_central) {
    return (
      <BCRequestView
        request={request}
        showProcessingModal={showProcessingModal}
        processingMinimized={processingMinimized}
        setProcessingMinimized={setProcessingMinimized}
        processingProgress={processingProgress}
        processingLog={processingLog}
        onOpCoMoved={fetchRequest}
        onPosted={fetchRequest}
      />
    );
  }

  const documents = request.documents || [];
  const activeDocument = documents[activeDocTab];

  const formatAiLearningExampleValue = (value) => {
    if (value == null) return "[blank]";
    const asString = String(value);
    return asString === "" ? "[blank]" : asString;
  };

  const buildAiLearningChipInstruction = (suggestion, fieldOption) => {
    const baseInstruction = String(
      suggestion?.rule_content || suggestion?.label || "",
    );
    const originalValue = formatAiLearningExampleValue(
      fieldOption?.original_field_value,
    );
    const correctedValue = formatAiLearningExampleValue(
      fieldOption?.field_value,
    );
    const fieldName = fieldOption?.field_name
      ? `For field "${fieldOption.field_name}": `
      : "";
    const correctionExample = `Example correction: original=${originalValue}, corrected=${correctedValue}.`;

    if (baseInstruction) {
      return `${fieldName}${baseInstruction}. ${correctionExample}`;
    }
    return `${fieldName}${correctionExample}`;
  };

  const aiAssistSelectedField = aiAssistFieldOptions.find(
    (f) => String(f.id) === String(aiAssistSelectedFieldId),
  );
  const aiAssistSuggestionChips = (aiAssistSuggestions || []).map((s, idx) => {
    const instruction = buildAiLearningChipInstruction(
      s,
      aiAssistSelectedField,
    );
    const label = (s?.label || "").toString().trim() || `Suggestion ${idx + 1}`;
    return {
      key: `${idx}-${instruction}`,
      label,
      instruction,
    };
  });
  const aiAssistExistingRule = aiAssistSelectedField?.template_field_id
    ? rulesByTemplateFieldId[String(aiAssistSelectedField.template_field_id)]
    : null;
  const aiAssistHasCustomInstruction =
    (aiAssistCustomInstruction || "").trim().length > 0;
  const aiAssistHasSelectedSuggestion =
    (aiAssistSelectedRuleContent || "").trim().length > 0;
  const aiAssistHasSelectedField = aiAssistFieldOptions.some(
    (f) => String(f.id) === String(aiAssistSelectedFieldId),
  );
  const aiAssistHasExactlyOneInstructionSource =
    Number(aiAssistHasCustomInstruction) +
      Number(aiAssistHasSelectedSuggestion) ===
    1;

  return (
    <div
      className="-m-6 flex bg-gray-100 dark:bg-gray-950 overflow-hidden animate-page-in"
      style={{ height: "calc(100vh - 4rem)" }}
    >

      {showSaveModeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
            onClick={() => setShowSaveModeModal(false)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Choose Save Action
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Save changes to the database only, or save and apply AI learning.
            </p>

            <div className="space-y-3">
              <button
                onClick={handleChooseSaveInDb}
                disabled={loadingSaveModeOptions}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 text-left hover:border-navy-300 dark:hover:border-navy-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Save in DB
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Saves your edits without creating new AI-learning guidance.
                </p>
              </button>

              <button
                onClick={handleChooseSaveInDbAndAi}
                disabled={
                  loadingSaveModeOptions ||
                  !currentTemplateAiLearningEnabled ||
                  aiLearningCandidateCount !== 1
                }
                className={`w-full px-4 py-3 rounded-lg border text-left transition-colors ${
                  loadingSaveModeOptions
                    ? "border-gray-200 dark:border-gray-700 bg-gray-100/70 dark:bg-gray-800/60 cursor-not-allowed opacity-70"
                    : currentTemplateAiLearningEnabled &&
                        aiLearningCandidateCount === 1
                      ? "border-indigo-200 dark:border-indigo-700 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20"
                      : "border-gray-200 dark:border-gray-700 bg-gray-100/70 dark:bg-gray-800/60 cursor-not-allowed opacity-70"
                }`}
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-indigo-500" />
                  Save in DB and Apply AI Learning
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Saves edits and applies learning based on your corrections.
                </p>
                {loadingSaveModeOptions && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                    Checking AI learning setting...
                  </p>
                )}
                {!loadingSaveModeOptions &&
                  !currentTemplateAiLearningEnabled && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">
                      Enable template AI learning first in Template settings.
                    </p>
                  )}
                {!loadingSaveModeOptions &&
                  currentTemplateAiLearningEnabled &&
                  aiLearningCandidateCount === 0 && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">
                      Edit exactly one field to use AI learning.
                    </p>
                  )}
                {!loadingSaveModeOptions &&
                  currentTemplateAiLearningEnabled &&
                  aiLearningCandidateCount > 1 && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">
                      Only single field learning is allowed. Keep one edited
                      field to continue.
                    </p>
                  )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAiLearningAssistModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh] border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-gray-900 dark:text-white font-medium">
                <BrainCircuit className="w-5 h-5 text-blue-500" />
                AI Learning
              </div>
              {!saving && (
                <button
                  onClick={() => {
                    setShowAiLearningAssistModal(false);
                    setPendingAiSaveReason("");
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label="Close AI Learning"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-3 rounded-lg text-sm mb-4">
                AI will learn from these corrections for future documents of
                this template. Please select the reasoning below.
                <span className="block mt-1 text-[11px] text-blue-700/80 dark:text-blue-300/70">
                  Tip: each rule you pick (or type) applies on the very next run
                  for this template.
                </span>
              </div>

              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                  Changed Fields
                </p>
                <div className="flex flex-wrap gap-2">
                  {aiAssistFieldOptions.map((fieldOpt) => (
                    <button
                      key={fieldOpt.id}
                      onClick={() => {
                        setAiAssistSelectedFieldId(fieldOpt.id);
                        loadAiLearningSuggestionsForField(fieldOpt);
                      }}
                      className={`px-2.5 py-1.5 rounded-md text-xs border transition-colors ${
                        String(aiAssistSelectedFieldId) === String(fieldOpt.id)
                          ? "border-blue-300 dark:border-blue-700 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200"
                          : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-600"
                      }`}
                    >
                      {fieldOpt.field_name}
                    </button>
                  ))}
                </div>
              </div>

              {aiAssistExistingRule?.instruction && (
                <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-[11px] text-amber-800 dark:text-amber-300">
                  <BrainCircuit className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">
                      You already taught this field.
                    </span>{" "}
                    Saving will update the existing rule:
                    <span className="block mt-0.5 italic text-amber-700/90 dark:text-amber-300/80 break-words">
                      "{aiAssistExistingRule.instruction}"
                    </span>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                  AI Suggested Rules
                </p>
                {aiAssistLoading ? (
                  <div className="text-xs text-blue-500 flex items-center gap-1 mt-2">
                    <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent text-blue-600 rounded-full" />
                    Analyzing correction...
                  </div>
                ) : !aiAssistHasSelectedField ? (
                  <div className="text-xs text-gray-500 mt-2 italic">
                    Select a changed field to load AI suggestions.
                  </div>
                ) : aiAssistSuggestionChips.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {aiAssistSuggestionChips.map((chip) => {
                      const isSelected =
                        aiAssistSelectedRuleContent === chip.instruction;
                      return (
                        <button
                          key={chip.key}
                          onClick={() => {
                            setAiAssistCustomInstruction("");
                            setAiAssistSelectedRuleContent((prev) =>
                              prev === chip.instruction ? "" : chip.instruction,
                            );
                          }}
                          className={`px-3 py-1.5 rounded-full text-[11px] border transition-colors ${
                            isSelected
                              ? "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700"
                              : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                          title={chip.label}
                        >
                          {chip.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 mt-2 italic">
                    No specific learning rules generated for this correction.
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 block uppercase tracking-wide">
                  Custom Instruction
                </label>
                <textarea
                  value={aiAssistCustomInstruction}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setAiAssistCustomInstruction(nextValue);
                    if (nextValue.trim() && aiAssistSelectedRuleContent) {
                      setAiAssistSelectedRuleContent("");
                    }
                  }}
                  placeholder="Or type a custom instruction (e.g., 'If not available use NA')"
                  rows={3}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                />
                <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                  Choose exactly one: AI suggestion chip or custom instruction.
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={() => {
                  setShowAiLearningAssistModal(false);
                  setPendingAiSaveReason("");
                }}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyAiLearningSave}
                disabled={
                  saving ||
                  !aiAssistHasSelectedField ||
                  !aiAssistHasExactlyOneInstructionSource
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                    Saving...
                  </>
                ) : (
                  "Confirm & Save"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Reason Modal */}
      {showSaveReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
            onClick={() => {
              if (!saving) {
                setShowSaveReasonModal(false);
              }
            }}
          ></div>

          {/* Modal Content */}
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Save Changes
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Please provide a reason for the changes you are making. This will
              be recorded in the audit log.
            </p>

            <textarea
              value={saveReason}
              onChange={(e) => setSaveReason(e.target.value)}
              placeholder="Enter reason for changes..."
              disabled={saving}
              className={`w-full h-24 px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none mb-4 dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-500 ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
              autoFocus
            />

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowSaveReasonModal(false);
                  setSaveReason("");
                }}
                disabled={saving}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  saving
                    ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveWithReason}
                disabled={!saveReason.trim() || saving}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-1.5 ${
                  saveReason.trim() && !saving
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {saving ? "Saving..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Reason Modal */}
      {showCancelReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
            onClick={() => {
              if (!cancelling) {
                setShowCancelReasonModal(false);
                setCancelError("");
              }
            }}
          ></div>

          {/* Modal Content */}
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Cancel Request
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Please provide a reason for cancelling this request. This will be
              recorded in the audit log.
            </p>

            {cancelError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-700 dark:text-red-300">
                  {cancelError}
                </span>
              </div>
            )}

            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason for cancellation..."
              disabled={cancelling}
              className={`w-full h-24 px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none mb-4 dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-500 ${cancelling ? "opacity-50 cursor-not-allowed" : ""}`}
              autoFocus
            />

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelReasonModal(false);
                  setCancelReason("");
                  setCancelError("");
                }}
                disabled={cancelling}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  cancelling
                    ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Go Back
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancelReason.trim() || cancelling}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-1.5 ${
                  cancelReason.trim() && !cancelling
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                {cancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                {cancelling ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* AI Extraction Progress Bar — sits at the very top, directly below the app navbar */}
        <div
          className={`overflow-hidden transition-all duration-500 ease-out ${
            showProcessingModal ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="relative bg-white dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="relative flex items-center gap-4">
              {/* The Xtract mark while the AI extracts (a ring if rebranded) */}
              <div className="w-11 h-11 flex items-center justify-center shrink-0">
                <ExtractLoader size={40} />
              </div>

              {/* Label + message */}
              <div className="flex flex-col min-w-0 shrink-0 w-56">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 tracking-tight">
                  <Sparkles className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                  AI is Extracting Details
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {processingProgress.message || "Starting…"}
                </span>
              </div>

              {/* Progress bar - fills remaining width */}
              <div className="flex-1 flex items-center gap-3 min-w-0">
                <div className="relative flex-1 h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden ring-1 ring-black/5 dark:ring-white/10">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${Math.max(processingProgress.percent, 2)}%`,
                      background: "linear-gradient(90deg, var(--ai-gradient-from), var(--ai-gradient-to))",
                    }}
                  >
                    <div className="absolute inset-0 progress-shimmer rounded-full"></div>
                  </div>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums shrink-0 w-12 text-right">
                  {processingProgress.percent}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Background Document Processing Banner - for a later job on an
            already-reviewed request (documentsStillProcessing). Lives at the
            very top alongside the initial extraction banner above, rather
            than lower in the page, so processing status is always in the
            same place. Completion is announced via a toast instead of a
            persistent banner (see the job-status poll above), so this only
            needs to render the "still running" state. */}
        {processingComplete && refreshHeldForEdits && (
          <div className="border-b px-4 py-2.5 flex-shrink-0 bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 dark:from-green-950/40 dark:via-emerald-950/40 dark:to-green-950/40 border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold flex items-center gap-1.5 text-green-800 dark:text-green-200">
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />{" "}
                  All documents have been processed
                </span>
                <span className="text-xs truncate text-green-600/80 dark:text-green-300/70">
                  New extracted fields are available, but you have unsaved
                  changes — refresh to load the latest data (this will discard
                  your edits).
                </span>
              </div>
              <button
                type="button"
                onClick={handleProcessingRefresh}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 hover:bg-green-700 text-white transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh Now
              </button>
            </div>
          </div>
        )}

        {documentsStillProcessing && (
          <div className="border-b px-4 py-2.5 flex-shrink-0 bg-gradient-to-r from-navy-50 via-navy-100/60 to-navy-50 dark:from-navy-900/50 dark:via-navy-800/50 dark:to-navy-900/50 border-navy-200 dark:border-navy-800">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                {/* Animated icon */}
                <div className="relative shrink-0">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shadow-sm ring-1 ring-white/10 bg-gradient-to-br from-navy-500 to-navy-700">
                    <Bot className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-navy-500 rounded-full animate-ping"></span>
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-navy-500 rounded-full"></span>
                </div>

                {/* Message */}
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold flex items-center gap-1.5 text-navy-800 dark:text-navy-200">
                    <Sparkles className="h-3.5 w-3.5 text-navy-500 dark:text-navy-400 animate-pulse" />{" "}
                    AI is still processing remaining documents
                  </span>
                  <span className="text-xs truncate text-navy-600/90 dark:text-navy-300/70">
                    {processingProgress.message ||
                      "Extracting and merging fields from additional documents…"}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="relative w-32 h-1.5 bg-navy-200/70 dark:bg-navy-800 rounded-full overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-cyan-400 rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${Math.max(processingProgress.percent, 10)}%`,
                      }}
                    >
                      <div className="absolute inset-0 progress-shimmer rounded-full"></div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-navy-700 dark:text-navy-300 tabular-nums">
                    {processingProgress.percent}%
                  </span>
                </div>
                {/* Spinning loader */}
                <Loader2 className="h-4 w-4 animate-spin text-navy-500" />
              </div>
            </div>
          </div>
        )}

        {/* Unified Request Header: icon/title/description (left), actions (right), meta chips below */}
        <div className="relative overflow-hidden bg-gradient-to-r from-navy-100/90 via-navy-50/70 to-navy-100/80 dark:from-gray-900/80 dark:via-gray-800/60 dark:to-gray-900/70 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 animate-header-in">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, var(--tw-color-navy-900, #0f2942) 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-navy-600 dark:bg-navy-500"
          />
          <div
            className={`relative z-10 pl-6 pr-5 flex items-start justify-between gap-4 flex-wrap transition-[padding] duration-300 ease-out ${
              isHeaderCollapsed ? "py-2.5" : "pt-4 pb-3"
            }`}
          >
            {/* LEFT: icon badge + subject / sender / description — edit affordance scoped to just this block */}
            <div
              className={`group/reqinfo relative flex min-w-0 flex-1 basis-[280px] ${
                isHeaderCollapsed ? "items-center gap-3" : "items-start gap-3.5"
              }`}
            >
              <div
                className={`hidden sm:flex flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-600 to-navy-800 text-white shadow-md ring-1 ring-navy-900/10 transition-all ${
                  isHeaderCollapsed ? "h-9 w-9" : "h-11 w-11"
                }`}
              >
                <FileText className={isHeaderCollapsed ? "h-4 w-4" : "h-5 w-5"} />
              </div>
              <div className="min-w-0 flex-1 flex flex-col gap-2">
                {/* Subject (title) with inline edit toggle */}
                <div className="flex items-center gap-1.5">
                  <h1 className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100 leading-snug break-words">
                    {editedMetadata.title || (
                      <span className="text-gray-400 dark:text-gray-500 font-normal italic">
                        Untitled Request
                      </span>
                    )}
                  </h1>
                  <button
                    type="button"
                    onClick={() => setIsHeaderCollapsed((prev) => !prev)}
                    title={isHeaderCollapsed ? "Expand details" : "Collapse details"}
                    className="flex-shrink-0 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-navy-600 dark:hover:text-navy-400 hover:bg-navy-50 dark:hover:bg-navy-900/20 transition-colors"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isHeaderCollapsed ? "-rotate-90" : ""}`}
                    />
                  </button>
                  <OpCoBadge request={request} />
                  <MoveOpCoButton request={request} onMoved={fetchRequest} />
                </div>

                {/* Description (email body) */}
                {!isHeaderCollapsed && editedMetadata.description && (
                  <div className="flex items-start gap-2">
                    <div className="min-w-0">
                      <p
                        className={`text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed break-words whitespace-pre-wrap ${
                          isDescriptionExpanded
                            ? "max-h-40 overflow-y-auto pr-1"
                            : "line-clamp-2"
                        }`}
                      >
                        {editedMetadata.description}
                      </p>
                      {editedMetadata.description &&
                        editedMetadata.description.length > 140 && (
                          <button
                            type="button"
                            onClick={() =>
                              setIsDescriptionExpanded((prev) => !prev)
                            }
                            className="mt-0.5 text-[11px] font-semibold text-navy-600 dark:text-navy-400 hover:underline"
                          >
                            {isDescriptionExpanded ? "Show less" : "Show more"}
                          </button>
                        )}
                    </div>
                  </div>
                )}

                {/* Template + File + e-mail sender — unified, symmetric chip row */}
                {!isHeaderCollapsed && (request?.template_name ||
                  activeDocument?.filename || request?.email?.sender) && (
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    {request?.template_name && (
                      <span className="inline-flex items-center gap-1.5 h-7 max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5">
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                          <FileSpreadsheet className="h-2.5 w-2.5" />
                        </span>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[180px]">
                          {request.template_name}
                        </span>
                      </span>
                    )}
                    {activeDocument?.filename && (
                      <span className="inline-flex items-center gap-1.5 h-7 pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 max-w-[220px] transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5">
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                          <FileText className="h-2.5 w-2.5" />
                        </span>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate">
                          {activeDocument.filename}
                        </span>
                      </span>
                    )}
                    {request?.email?.sender && (
                      <span
                        className="inline-flex items-center gap-1.5 h-7 pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 max-w-[260px] transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
                        title={[
                          `From: ${request.email.sender}`,
                          request.email.subject && `Subject: ${request.email.subject}`,
                          request.email.received_at &&
                            `Received: ${new Date(request.email.received_at).toLocaleString()}`,
                        ]
                          .filter(Boolean)
                          .join("\n")}
                      >
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                          <Mail className="h-2.5 w-2.5" />
                        </span>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate">
                          {request.email.sender}
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: status indicators (top) + action buttons (below) — vary based on request status/mode */}
            <div
              className={`flex flex-shrink-0 gap-2 ${
                isHeaderCollapsed
                  ? "flex-row items-center"
                  : "flex-col items-end"
              }`}
            >
              {!isHeaderCollapsed && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 shrink min-w-0 overflow-hidden whitespace-nowrap">
                  {/* Confidence badge + hover legend */}
                  <span className="group relative inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/40 cursor-default">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${!hasConfidenceData ? "bg-gray-400" : avgConfidence >= 90 ? "bg-green-500" : avgConfidence >= 70 ? "bg-orange-500" : "bg-red-500"}`}
                    />
                    <span className="text-xs whitespace-nowrap text-gray-400 dark:text-gray-500">
                      Confidence{" "}
                      {hasConfidenceData ? (
                        <span className={avgConfidence >= 90 ? "text-green-600 dark:text-green-400" : avgConfidence >= 70 ? "text-orange-600 dark:text-orange-400" : "text-red-600 dark:text-red-400"}>
                          {avgConfidence}%
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">No data</span>
                      )}
                    </span>

                    {/* Confidence legend — shown on hover over the Confidence badge */}
                    <div className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150">
                      <div className="flex items-center gap-2.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-500 dark:text-gray-400">
                            &gt;90%
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                          <span className="text-gray-500 dark:text-gray-400">
                            70-89%
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-gray-500 dark:text-gray-400">
                            &lt;70%
                          </span>
                        </div>
                      </div>
                    </div>
                  </span>
                  {/* Saved timestamp / pending indicators — informational, stays above the action buttons */}
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/40 text-gray-400 dark:text-gray-500">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="text-xs whitespace-nowrap">
                      Saved{" "}
                      {request?.updated_at
                        ? formatTimeAgoUtil(request.updated_at)
                        : "N/A"}
                    </span>
                  </span>
                  {aiLearningDebugStatus && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-indigo-50 dark:bg-indigo-900/20 text-xs text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                      {aiLearningDebugStatus}
                    </span>
                  )}
                  {changedFieldCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 text-xs font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                      {changedFieldCount} field{changedFieldCount !== 1 ? "s" : ""}{" "}
                      pending
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Show different buttons based on request status */}
            {request?.status === "cancelled" ? (
              // Cancelled request: No edit actions available
              <span className="text-sm text-gray-500 dark:text-gray-400 italic flex items-center gap-1.5">
                <XCircle className="h-4 w-4" />
                Request Cancelled — Read Only
              </span>
            ) : request?.status === 'pending' ? (
              // Pending request: Show Extract button
              <button
                onClick={handleExtract}
                disabled={isReader || publishing || !documents?.length}
                className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                  isReader || publishing || !documents?.length
                    ? 'bg-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                title={isReader ? 'Read-only access' : !documents?.length ? 'No documents to extract' : 'Start AI extraction'}
              >
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                {publishing ? 'Extracting...' : 'Extract'}
              </button>
            ) : isRequestLocked(request?.status) ? (
              // Terminal (approved/rejected/failed/completed): finalized, no
              // edit action offered — the backend rejects PUT /requests/:id
              // and /requests/:id/fields for these anyway (see
              // utils/requestStatus.js), so an Edit button here would just
              // walk the user into a save that 409s. Same read-only pattern
              // as the "cancelled" case above.
              <span className="text-sm text-gray-500 dark:text-gray-400 italic flex items-center gap-1.5">
                {(request?.status === "approved" || request?.status === "completed") &&
                failedConnectorCount === 0 ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertCircle
                    className={`h-4 w-4 ${failedConnectorCount > 0 ? "text-amber-500" : ""}`}
                  />
                )}
                {request?.status === "approved" && failedConnectorCount > 0
                  ? `Request Approved — not delivered to ${failedConnectorCount} connector${failedConnectorCount === 1 ? "" : "s"} (see Exports)`
                  : request?.status === "approved"
                  ? "Request Published"
                  : request?.status === "completed"
                    ? "Submission Successful — Read Only"
                    : request?.status === "rejected"
                      ? "Request Rejected — Read Only"
                      : "Submission Failed — Read Only"}
              </span>
            ) : (request?.status === 'published' || request?.status === 'pending_approval' || request?.status === 'in_review') ? (
              // Published / Pending Approval / In Review: fields are directly
              // editable — Discard / Save (+ Publish and Cancel once the
              // request is awaiting approval). No Edit toggle stands in front
              // of them; the backend accepts edits on all three statuses.
              <>
                <button
                  onClick={handleDiscardChanges}
                  disabled={isReader || saving || backgroundJobActive}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                    isReader || saving || backgroundJobActive
                      ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                      : "bg-red-50 dark:bg-red-400/10 text-red-600 dark:text-red-300 dark:border dark:border-red-400/30 hover:bg-red-100 dark:hover:bg-red-400/20"
                  }`}
                >
                  <XCircle className="h-4 w-4" />
                  Discard Changes
                </button>
                <button
                  onClick={() =>
                    openSaveModeSelector({
                      requireReason: true,
                    })
                  }
                  disabled={
                    isReader ||
                    saving ||
                    !hasUnsavedChanges() ||
                    backgroundJobActive
                  }
                  className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                    !isReader &&
                    hasUnsavedChanges() &&
                    !saving &&
                    !backgroundJobActive
                      ? "bg-navy-600 hover:bg-navy-700"
                      : "bg-gray-400 cursor-not-allowed opacity-50"
                  }`}
                  title={
                    backgroundJobActive
                      ? "Wait for background processing to finish, then refresh"
                      : hasUnsavedChanges()
                        ? "Save changes"
                        : "No changes to save"
                  }
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? "Saving..." : "Save"}
                </button>
                {(request?.status === "pending_approval" ||
                  request?.status === "in_review") && (
                  <>
                    {userRole !== "sender" && (
                      <button
                        onClick={handlePublish}
                        disabled={
                          isReader ||
                          publishing ||
                          saving ||
                          hasEmptyRequiredFields ||
                          backgroundJobActive
                        }
                        className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                          isReader ||
                          publishing ||
                          saving ||
                          hasEmptyRequiredFields ||
                          backgroundJobActive
                            ? "bg-gray-400 cursor-not-allowed opacity-50"
                            : "bg-navy-700 hover:bg-navy-800"
                        }`}
                        title={
                          isReader
                            ? "Read-only access"
                            : backgroundJobActive
                              ? "Wait for background processing to finish, then refresh"
                              : hasEmptyRequiredFields
                                ? `${emptyRequiredFields.length} required field(s) still empty`
                                : "Approve and publish"
                        }
                      >
                        {publishing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        {publishing ? "Publishing..." : "Publish"}
                      </button>
                    )}
                    {request?.can_cancel !== false && (
                      <button
                        onClick={() => setShowCancelReasonModal(true)}
                        disabled={
                          isReader || publishing || saving || backgroundJobActive
                        }
                        className={`px-4 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                          isReader || publishing || saving || backgroundJobActive
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                            : "bg-red-50 dark:bg-red-400/10 text-red-600 dark:text-red-300 dark:border dark:border-red-400/30 hover:bg-red-100 dark:hover:bg-red-400/20"
                        }`}
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </button>
                    )}
                  </>
                )}
              </>
            ) : (
              // Non-published request: Show buttons based on mandate_review and role
              <>
                {externalReview && userRole !== "admin" ? (
                  // Review belongs to a connected application. Offering a
                  // publish button here would only produce a 403, and two
                  // reviewers publishing the same request would silently
                  // discard one set of corrections.
                  <span className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 italic">
                    Reviewed in the connected application
                  </span>
                ) : mandateReview && request?.status === "reviewing" ? (
                  // mandate_review ON + reviewing: Show buttons based on role
                  <>
                    {userRole !== "sender" && (
                      <button
                        onClick={handlePublish}
                        disabled={
                          isReader ||
                          publishing ||
                          saving ||
                          sendingForReview ||
                          hasEmptyRequiredFields ||
                          backgroundJobActive
                        }
                        className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                          isReader ||
                          publishing ||
                          saving ||
                          sendingForReview ||
                          hasEmptyRequiredFields ||
                          backgroundJobActive
                            ? "bg-gray-400 cursor-not-allowed opacity-50"
                            : "bg-navy-700 hover:bg-navy-800"
                        }`}
                        title={
                          isReader
                            ? "Read-only access"
                            : backgroundJobActive
                              ? "Wait for background processing to finish, then refresh"
                              : hasEmptyRequiredFields
                                ? `${emptyRequiredFields.length} required field(s) still empty`
                                : "Publish changes"
                        }
                      >
                        {publishing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        {publishing ? "Publishing..." : "Publish"}
                      </button>
                    )}
                    <button
                      onClick={handleSendForReview}
                      disabled={
                        isReader ||
                        sendingForReview ||
                        saving ||
                        publishing ||
                        backgroundJobActive
                      }
                      className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                        isReader ||
                        sendingForReview ||
                        saving ||
                        publishing ||
                        backgroundJobActive
                          ? "bg-gray-400 cursor-not-allowed opacity-50"
                          : "bg-navy-500 hover:bg-navy-600"
                      }`}
                      title={
                        isReader
                          ? "Read-only access"
                          : backgroundJobActive
                            ? "Wait for background processing to finish, then refresh"
                            : "Send for review and approval"
                      }
                    >
                      {sendingForReview ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <SendHorizonal className="h-4 w-4" />
                      )}
                      {sendingForReview ? "Sending..." : "Send for Review"}
                    </button>
                    {request?.can_cancel !== false && (
                      <button
                        onClick={() => setShowCancelReasonModal(true)}
                        disabled={
                          isReader ||
                          sendingForReview ||
                          saving ||
                          publishing ||
                          backgroundJobActive
                        }
                        className={`px-4 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                          isReader ||
                          sendingForReview ||
                          saving ||
                          publishing ||
                          backgroundJobActive
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                            : "bg-red-50 dark:bg-red-400/10 text-red-600 dark:text-red-300 dark:border dark:border-red-400/30 hover:bg-red-100 dark:hover:bg-red-400/20"
                        }`}
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={handleSaveChanges}
                      disabled={
                        isReader ||
                        saving ||
                        publishing ||
                        sendingForReview ||
                        !hasUnsavedChanges() ||
                        backgroundJobActive
                      }
                      className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                        !isReader &&
                        hasUnsavedChanges() &&
                        !saving &&
                        !sendingForReview &&
                        !publishing &&
                        !backgroundJobActive
                          ? "bg-navy-600 hover:bg-navy-700"
                          : "bg-gray-400 cursor-not-allowed opacity-50"
                      }`}
                      title={
                        backgroundJobActive
                          ? "Wait for background processing to finish, then refresh"
                          : hasUnsavedChanges()
                            ? "Save changes"
                            : "No changes to save"
                      }
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </>
                ) : (
                  // mandate_review OFF or non-reviewing status: Show buttons based on role
                  <>
                    {userRole !== "sender" && (
                      <button
                        onClick={handlePublish}
                        disabled={
                          isReader ||
                          publishing ||
                          saving ||
                          hasEmptyRequiredFields ||
                          backgroundJobActive
                        }
                        className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                          isReader ||
                          publishing ||
                          saving ||
                          hasEmptyRequiredFields ||
                          backgroundJobActive
                            ? "bg-gray-400 cursor-not-allowed opacity-50"
                            : "bg-navy-700 hover:bg-navy-800"
                        }`}
                        title={
                          isReader
                            ? "Read-only access"
                            : backgroundJobActive
                              ? "Wait for background processing to finish, then refresh"
                              : hasEmptyRequiredFields
                                ? `${emptyRequiredFields.length} required field(s) still empty`
                                : "Publish changes"
                        }
                      >
                        {publishing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        {publishing ? "Publishing..." : "Publish"}
                      </button>
                    )}
                    {request?.can_cancel !== false && (
                      <button
                        onClick={() => setShowCancelReasonModal(true)}
                        disabled={isReader || publishing || saving || backgroundJobActive}
                        className={`px-4 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                          isReader || publishing || saving || backgroundJobActive
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                            : "bg-red-50 dark:bg-red-400/10 text-red-600 dark:text-red-300 dark:border dark:border-red-400/30 hover:bg-red-100 dark:hover:bg-red-400/20"
                        }`}
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={handleSaveChanges}
                      disabled={
                        isReader ||
                        saving ||
                        publishing ||
                        !hasUnsavedChanges() ||
                        backgroundJobActive
                      }
                      className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all duration-200 active:scale-[0.97] shadow-sm hover:shadow ${
                        !isReader &&
                        hasUnsavedChanges() &&
                        !saving &&
                        !publishing &&
                        !backgroundJobActive
                          ? "bg-navy-600 hover:bg-navy-700"
                          : "bg-gray-400 cursor-not-allowed opacity-50"
                      }`}
                      title={
                        isReader
                          ? "Read-only access"
                          : backgroundJobActive
                            ? "Wait for background processing to finish, then refresh"
                            : hasUnsavedChanges()
                              ? "Save changes"
                              : "No changes to save"
                      }
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </>
                )}
              </>
            )}
              </div>
            </div>
          </div>
        </div>
        <UnassignedOpCoBanner request={request} onMoved={fetchRequest} />

        {/* Content below action bar */}

        <div className="flex-1 flex overflow-hidden bg-white dark:bg-gray-900">
          {/* Center Panel - Document Viewer + Extracted Fields side by side */}
          <div className="flex-1 flex flex-col bg-gray-100 dark:bg-gray-950 overflow-hidden">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.csv,.txt,.msg,.eml,.json,.xml,.png,.jpg,.jpeg,.gif"
              onChange={handleDocumentUpload}
            />

            {/* Main content: Extracted Fields left, PDF right */}
            <div
              ref={mainContentRef}
              className={`flex-1 flex overflow-hidden min-w-0 ${isDraggingDivider ? "select-none cursor-col-resize" : ""}`}
            >
              {/* Left Panel - Extracted Data Fields */}
              <div
                style={{
                  width: isPdfViewerVisible ? `${leftPanelPct}%` : "100%",
                }}
                className={`flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden ${isDraggingDivider ? "" : "transition-[width] duration-300 ease-out"}`}
              >
                {/* Extracted Fields Header */}
                <div
                  className={`px-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-800/60 dark:to-gray-900 ${
                    leftPanelTab === "fields" ? "py-3.5" : "pt-3.5"
                  }`}
                >
                  {/* Tabs: Fields / Activity / Exports / AI Insights */}
                  <RequestPanelTabs
                    tabs={[
                      { key: "fields", label: "Fields", icon: ClipboardList, badgeCount: emptyRequiredFields.length },
                      { key: "activity", label: "Activity", icon: Activity },
                      ...(features.connectors
                        ? [{ key: "exports", label: "Exports", icon: Server, badgeCount: outputDeliveries.filter((d) => d.status === "failed").length + failedConnectorCount }]
                        : []),
                      { key: "ai_ops", label: "AI Insights", icon: Sparkles },
                    ]}
                    activeTab={leftPanelTab}
                    onTabChange={setLeftPanelTab}
                    isDocViewerVisible={isPdfViewerVisible}
                    onToggleDocViewer={() =>
                      setIsPdfViewerVisible((prev) => {
                        const next = !prev;
                        if (next) setLeftPanelPct(50);
                        return next;
                      })
                    }
                  />
                  {leftPanelTab === "fields" && (
                    <FieldsPanelHeader
                      fieldCount={allGroupedFields.length}
                      emptyRequiredCount={emptyRequiredFields.length}
                      onJumpToEmptyRequired={() => scrollToEmptyRequiredField(emptyRequiredFields)}
                      searchQuery={fieldSearchQuery}
                      onSearchChange={setFieldSearchQuery}
                      extractionFilter={fieldsExtractionFilter}
                      onExtractionFilterChange={setFieldsExtractionFilter}
                      extractedCount={
                        (fieldsViewFilter === "mandatory"
                          ? allGroupedFields.filter((f) => f.is_required)
                          : fieldsViewFilter === "custom"
                            ? allGroupedFields.filter((f) => f.source_type === "user_input")
                            : allGroupedFields
                        ).filter((f) =>
                          ["document", "email_body", "llm_fallback", "ai_learning"].includes(f.source_type),
                        ).length
                      }
                      notExtractedCount={
                        (fieldsViewFilter === "mandatory"
                          ? allGroupedFields.filter((f) => f.is_required)
                          : fieldsViewFilter === "custom"
                            ? allGroupedFields.filter((f) => f.source_type === "user_input")
                            : allGroupedFields
                        ).filter((f) => !f.source_type || f.source_type === "pending").length
                      }
                      viewFilter={fieldsViewFilter}
                      onViewFilterChange={setFieldsViewFilter}
                      viewIndicatorStyle={fieldsViewIndicatorStyle}
                      viewTabRefs={fieldsViewTabRefs}
                    />
                  )}
                </div>

                {/* Scrollable Fields Content */}
                {leftPanelTab === "fields" && (
                <div className="flex-1 overflow-y-auto px-4 py-3 animate-panel-in">
                  <div className="space-y-5">
                    {orderedCategories.map((category, catIdx) => {
                      let fields = fieldsByCategory[category] || [];
                      // Apply field type filter (All vs Mandatory vs Custom)
                      if (fieldsViewFilter === "mandatory") {
                        fields = fields.filter((f) => f.is_required);
                      } else if (fieldsViewFilter === "custom") {
                        fields = fields.filter(
                          (f) => f.source_type === "user_input",
                        );
                      }
                      // Apply extraction status filter
                      if (fieldsExtractionFilter === "extracted") {
                        fields = fields.filter((f) =>
                          [
                            "document",
                            "email_body",
                            "llm_fallback",
                            "ai_learning",
                            "derived",
                          ].includes(f.source_type),
                        );
                      } else if (fieldsExtractionFilter === "not_extracted") {
                        fields = fields.filter(
                          (f) => !f.source_type || f.source_type === "pending",
                        );
                      }
                      // Apply field search filter — matches field label/value, OR the section/category name (in which case keep all fields in that section)
                      if (fieldSearchQuery.trim()) {
                        const q = fieldSearchQuery.toLowerCase();
                        const categoryMatches = getCategoryDisplayName(category)
                          .toLowerCase()
                          .includes(q);
                        if (!categoryMatches) {
                          fields = fields.filter(
                            (f) =>
                              (f.display_label || f.field_name || "")
                                .toLowerCase()
                                .includes(q) ||
                              (f.value || "")
                                .toString()
                                .toLowerCase()
                                .includes(q),
                          );
                        }
                      }
                      if (fields.length === 0) return null;
                      const color = getCategoryColor(category);
                      const isCollapsed = !!collapsedCategories[category];
                      const accentColor =
                        color && color !== "gray" ? color : "#64748b";
                      return (
                        <div
                          key={category}
                          style={{ animationDelay: `${Math.min(catIdx, 8) * 40}ms` }}
                          className={`group/catcard animate-card-in rounded-xl border bg-white dark:bg-gray-900/40 overflow-hidden transition-all duration-200 ${
                            isCollapsed
                              ? "border-gray-200 dark:border-gray-700/70 shadow-sm"
                              : "border-gray-200 dark:border-gray-700/70 shadow-md shadow-gray-900/[0.03] dark:shadow-black/20"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setCollapsedCategories((prev) => ({
                                ...prev,
                                [category]: !prev[category],
                              }))
                            }
                            className="relative w-full flex items-center justify-between gap-2 pl-4 pr-3 py-3 bg-gray-100 dark:bg-gray-800/80 hover:bg-gray-200 dark:hover:bg-gray-700/80 transition-colors duration-200 group/cathdr focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400/50 focus-visible:ring-inset"
                          >
                            <span
                              className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full transition-all duration-200 group-hover/cathdr:w-1.5"
                              style={{ backgroundColor: accentColor }}
                            />
                            <div className="flex items-center gap-2.5 min-w-0">
                              <h4 className="text-[13px] font-bold tracking-tight text-gray-900 dark:text-gray-100 truncate">
                                {getCategoryDisplayName(category)}
                              </h4>
                              <span
                                className="inline-flex items-center justify-center min-w-[1.375rem] h-[1.15rem] px-1.5 rounded-full text-[10.5px] font-bold tabular-nums flex-shrink-0 border"
                                style={{
                                  backgroundColor: `${accentColor}1a`,
                                  color: accentColor,
                                  borderColor: `${accentColor}33`,
                                }}
                              >
                                {fields.length}
                              </span>
                            </div>
                            <span className="flex items-center justify-center h-6 w-6 rounded-full text-gray-400 dark:text-gray-500 group-hover/cathdr:bg-gray-200/70 dark:group-hover/cathdr:bg-gray-600/50 group-hover/cathdr:text-gray-600 dark:group-hover/cathdr:text-gray-200 transition-colors duration-200 flex-shrink-0">
                              <ChevronDown
                                className={`h-4 w-4 transition-transform duration-300 ease-out ${
                                  isCollapsed ? "-rotate-90" : "rotate-0"
                                }`}
                              />
                            </span>
                          </button>
                          <div
                            className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                              isCollapsed
                                ? "grid-rows-[0fr]"
                                : "grid-rows-[1fr]"
                            }`}
                          >
                            <div className="overflow-hidden">
                              <div
                                className={`p-3 ${!isPdfViewerVisible ? "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2" : "space-y-2"}`}
                              >
                            {fields.map((field) => {
                              // --- ui_properties grid-level overrides ---
                              const uiProps = field.ui_properties || {};
                              const gridStyle = !isPdfViewerVisible
                                ? (() => {
                                    const classes = [];
                                    if (
                                      uiProps.fullWidth ||
                                      uiProps.colSpan === "full"
                                    ) {
                                      classes.push(
                                        "col-span-1 lg:col-span-2 xl:col-span-3",
                                      );
                                    } else if (uiProps.colSpan === 2) {
                                      classes.push("col-span-1 lg:col-span-2");
                                    }
                                    return classes.join(" ");
                                  })()
                                : "";
                              const inlineStyle = {};
                              if (uiProps.minWidth)
                                inlineStyle.minWidth = uiProps.minWidth;
                              if (uiProps.maxWidth)
                                inlineStyle.maxWidth = uiProps.maxWidth;

                              return (
                                <div
                                  key={
                                    field._is_placeholder
                                      ? `pending-${field.field_name}`
                                      : field.id
                                  }
                                  ref={(el) => {
                                    if (field._is_placeholder) return;
                                    fieldRowRefs.current[String(field.id)] = el;
                                  }}
                                  className={`${gridStyle} ${
                                    pulsedFieldId === String(field.id)
                                      ? "rounded-lg ring-2 ring-red-400 dark:ring-red-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 animate-pulse"
                                      : ""
                                  }`}
                                  style={inlineStyle}
                                >
                                  <FieldInput
                                    field={field}
                                    originalField={(originalFields || []).find(
                                      (of) =>
                                        String(of?.id) === String(field?.id),
                                    )}
                                    learningRule={
                                      rulesByTemplateFieldId[
                                        String(field?.template_field_id)
                                      ]
                                    }
                                    aiLearningUsage={
                                      field?.source_type === "ai_learning"
                                        ? aiLearningUsage
                                        : null
                                    }
                                    onClick={() => handleFieldClick(field)}
                                    onChange={handleFieldChange}
                                    isDate={
                                      field.field_type === "date" ||
                                      category === "date"
                                    }
                                    requestId={id}
                                    onAlternativeSelect={
                                      handleAlternativeSelect
                                    }
                                    onViewInDocument={handleViewInDocument}
                                    documents={documents}
                                    disabled={isFieldsDisabled}
                                    hasAuditHistoryFromParent={
                                      !!fieldsAuditMap[String(field.id)]
                                    }
                                  />
                                </div>
                              );
                            })}
                                {fields.length === 0 && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                    No fields in this category
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Processing Log — hidden in production */}
                    {SHOW_DEV_LOGS &&
                      processingLog.length > 0 && (
                        <div className="mb-4">
                          <button
                            onClick={() =>
                              setShowProcessingLog(!showProcessingLog)
                            }
                            className="flex items-center justify-between w-full mb-2"
                          >
                            <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                              <Clock className="w-3 h-3 text-blue-500" />
                              Processing Log
                              <span className="text-[10px] font-normal text-gray-400">
                                ({processingLog.length} steps)
                              </span>
                            </h4>
                            <ChevronDown
                              className={`w-3 h-3 text-gray-400 transition-transform ${showProcessingLog ? "rotate-180" : ""}`}
                            />
                          </button>
                          {showProcessingLog && (
                            <div className="bg-gray-900 dark:bg-black rounded-lg border border-gray-700 max-h-80 overflow-y-auto font-mono text-[11px] select-all">
                              {processingLog.map((step, idx) => (
                                <div
                                  key={idx}
                                  className="px-3 py-1 border-b border-gray-800 flex gap-2 hover:bg-gray-800"
                                >
                                  <span className="text-gray-500 whitespace-nowrap min-w-[50px]">
                                    {step.elapsed_seconds != null
                                      ? `${step.elapsed_seconds}s`
                                      : step.timestamp}
                                  </span>
                                  <span
                                    className={
                                      step.message?.includes("✅")
                                        ? "text-green-400"
                                        : step.message?.includes("❌")
                                          ? "text-red-400"
                                          : step.message?.includes("⚠️")
                                            ? "text-yellow-400"
                                            : step.message?.includes("⏳")
                                              ? "text-blue-400"
                                              : "text-gray-300"
                                    }
                                  >
                                    {step.message}
                                  </span>
                                </div>
                              ))}
                              <div ref={logBottomRef} />
                            </div>
                          )}
                          {!showProcessingLog && (
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                              {request?.last_job?.status === "completed"
                                ? `✅ Completed in ${processingLog[processingLog.length - 1]?.elapsed_seconds || "?"}s`
                                : request?.last_job?.status === "failed"
                                  ? `❌ Failed: ${request?.last_job?.error_message || "Unknown error"}`
                                  : "Processing..."}
                            </p>
                          )}
                        </div>
                      )}

                    {/* Activity Log and Export Status moved to their own tabs — see "Activity" / "Exports" tabs */}
                  </div>
                </div>
                )}

                {/* Activity Log Tab Panel */}
                {leftPanelTab === "activity" && (
                  <ActivityLogPanel
                    logs={activityLog}
                    loading={loadingAuditLogs}
                    onRefresh={fetchAuditLogs}
                  />
                )}

                {/* Exports Tab Panel */}
                {leftPanelTab === "exports" && (
                  <div className="flex-1 overflow-y-auto px-4 py-4 animate-panel-in">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        Export Status
                      </h3>
                      <button
                        onClick={fetchOutputDeliveries}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                      >
                        <RefreshCw
                          className={`w-3 h-3 ${loadingOutputDeliveries ? "animate-spin" : ""}`}
                        />
                        Refresh
                      </button>
                    </div>
                    <ConnectorDeliveriesPanel
                      requestId={id}
                      requestStatus={request?.status}
                      isAdmin={isAdmin}
                      refreshKey={request?.status}
                      onFailedCountChange={setFailedConnectorCount}
                    />
                    <div className="space-y-2">
                      {loadingOutputDeliveries ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                          <span className="ml-2 text-xs text-gray-400">
                            Loading export status...
                          </span>
                        </div>
                      ) : outputDeliveries.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <Server className="h-6 w-6 text-gray-300 dark:text-gray-600 mb-1.5" />
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {linkedOutputDestinations.length > 0
                              ? "Not yet exported to any output destination."
                              : "No output destination exports for this request"}
                          </p>
                        </div>
                      ) : (
                        outputDeliveries.map((delivery) => {
                          const isSuccess = delivery.status === "success";
                          const isFailed = delivery.status === "failed";
                          return (
                            <div
                              key={delivery.id}
                              className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span
                                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                        isSuccess
                                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                          : isFailed
                                            ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                      }`}
                                    >
                                      {isSuccess
                                        ? "Success"
                                        : isFailed
                                          ? "Failed"
                                          : "Pending"}
                                    </span>
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                      {delivery.destination_name ||
                                        "Unknown destination"}
                                    </p>
                                  </div>
                                  {delivery.generated_filename && (
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 font-mono truncate mt-0.5">
                                      {delivery.generated_filename}
                                    </p>
                                  )}
                                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                                    {delivery.completed_at
                                      ? formatDateTime(delivery.completed_at)
                                      : delivery.created_at
                                        ? formatDateTime(delivery.created_at)
                                        : "—"}
                                  </p>
                                  {isFailed && delivery.error_message && (
                                    (() => {
                                      // Errors are stored up to 2000 chars.
                                      // Rendering that inline in this narrow
                                      // column was unreadable, so show a
                                      // one-line summary and let it expand.
                                      const isOpen =
                                        expandedDeliveryError === delivery.id;
                                      const isLong =
                                        delivery.error_message.length > 120;
                                      return (
                                        <div className="mt-1">
                                          {isOpen || !isLong ? (
                                            <pre className="text-[10px] text-red-600 dark:text-red-400 whitespace-pre-wrap break-words font-mono bg-red-50 dark:bg-red-900/10 rounded p-1.5 max-h-40 overflow-auto">
                                              {delivery.error_message}
                                            </pre>
                                          ) : (
                                            <p className="text-[10px] text-red-600 dark:text-red-400 truncate">
                                              {delivery.error_message}
                                            </p>
                                          )}
                                          {isLong && (
                                            <button
                                              onClick={() =>
                                                setExpandedDeliveryError(
                                                  isOpen ? null : delivery.id,
                                                )
                                              }
                                              className="text-[10px] text-blue-600 hover:text-blue-800 font-medium mt-0.5"
                                            >
                                              {isOpen ? "Show less" : "Show full error"}
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })()
                                  )}
                                </div>
                                {isFailed && isAdmin && (
                                  <button
                                    onClick={() =>
                                      handleReExportDelivery(delivery)
                                    }
                                    disabled={
                                      reExportingDeliveryId === delivery.id
                                    }
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-navy-800 text-white text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                                  >
                                    {reExportingDeliveryId === delivery.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Send className="h-3 w-3" />
                                    )}
                                    Re-export
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Export now — the backend has always supported
                          exporting any request to any linked destination,
                          but the only UI affordance was "Re-export" on an
                          already-failed row. A destination linked after
                          approval was therefore unreachable. */}
                      {isAdmin && linkedOutputDestinations.length > 0 && (
                        <div className="pt-2 mt-1 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-1.5">
                            <select
                              value={manualExportDestinationId}
                              onChange={(e) =>
                                setManualExportDestinationId(e.target.value)
                              }
                              className="flex-1 min-w-0 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-[10px]"
                            >
                              <option value="">Export now to…</option>
                              {linkedOutputDestinations.map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.name}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={handleManualExport}
                              disabled={
                                !manualExportDestinationId || manualExporting
                              }
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-navy-800 text-white text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                            >
                              {manualExporting ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                              Export
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* AI Operations Tab Panel */}
                {leftPanelTab === "ai_ops" && (
                  <div className="flex-1 overflow-y-auto px-4 py-4 animate-panel-in">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        AI Insights
                      </h3>
                    </div>
                    <CostBreakdown requestId={id} embedded />
                  </div>
                )}
              </div>

              {/* Draggable divider — drag right to shrink/collapse the PDF viewer, drag left to shrink/hide the fields panel */}
              <div
                onMouseDown={isPdfViewerVisible ? handleDividerMouseDown : undefined}
                role="separator"
                aria-orientation="vertical"
                title="Drag to resize"
                className={`relative flex-shrink-0 bg-gray-200 dark:bg-gray-700 hover:bg-teal-400 dark:hover:bg-teal-500 active:bg-teal-500 group z-10 overflow-hidden ${
                  isDraggingDivider ? "" : "transition-[width,opacity] duration-300 ease-out"
                } ${
                  isPdfViewerVisible
                    ? "w-1 opacity-100 cursor-col-resize"
                    : "w-0 opacity-0 pointer-events-none"
                }`}
              >
                <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
              </div>

              {/* Right side: PDF viewer — scrolls continuously through all pages */}
              <div
                style={{
                  width: isPdfViewerVisible ? `${100 - leftPanelPct}%` : "0%",
                }}
                className={`flex flex-shrink-0 overflow-hidden min-w-0 ${
                  isDraggingDivider ? "" : "transition-[width,opacity] duration-300 ease-out"
                } ${isPdfViewerVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                  {/* Document Content */}
                  <div className="flex-1 bg-gray-300 dark:bg-[#2a2d30] overflow-auto p-4 flex items-start justify-center min-w-0">
                    {documentId && isPdfDocument(activeDocument) ? (
                      <PDFViewer
                        key={documentId}
                        documentId={documentId}
                        highlightedField={highlightedField}
                        onPageChange={setCurrentPage}
                        fileName={activeDocument?.filename}
                      />
                    ) : documentId &&
                      activeDocument &&
                      !isPdfDocument(activeDocument) ? (
                      <DocumentViewer
                        key={documentId}
                        documentId={documentId}
                        document={activeDocument}
                        toast={toast}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500">
                        <FileText className="h-16 w-16 mb-4 opacity-50" />
                        <p className="text-lg font-medium">
                          No document selected
                        </p>
                        <p className="text-sm mt-1">
                          Upload or select a document to view it here
                        </p>
                      </div>
                    )}
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sanitize initial value for numeric field types - strip non-numeric chars
function sanitizeNumericValue(val, fieldType) {
  if (!val) return val;
  if (
    fieldType === "number" ||
    fieldType === "integer" ||
    fieldType === "percentage"
  ) {
    // Strip everything except digits, minus, dot, and commas
    const cleaned = val.replace(/[^0-9.\-,]/g, "");
    return cleaned;
  }
  if (fieldType === "currency" || fieldType === "money") {
    // For currency, strip alpha chars from the amount portion only (preserve symbol)
    const parts = val.split(" ");
    if (parts.length > 1) {
      const symbol = parts[0];
      const amount = parts
        .slice(1)
        .join(" ")
        .replace(/[^0-9.\-,]/g, "");
      return `${symbol} ${amount}`.trim();
    }
    return val;
  }
  return val;
}

// Shared dropdown utility — auto-detects format and returns flat list + render function
// Supports:
//   1. Comma-separated string:  "Per Occurrence,Aggregate,Umbrella"
//   2. Flat array:              ["opt1", "opt2", "opt3"]
//   3. Grouped array:           [{group: "USA", items: ["NY", "CA"]}, {group: "Europe", items: ["UK", "FR"]}]
function parseDropdownOptions(raw) {
  let options = raw;
  // If it's a string, try JSON first, then fall back to comma-split
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) options = parsed;
      else
        options = raw
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
    } catch {
      options = raw
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
  }
  if (!Array.isArray(options) || options.length === 0) {
    return { flat: [], isGrouped: false, render: () => null };
  }
  const isGrouped =
    typeof options[0] === "object" &&
    options[0] !== null &&
    "group" in options[0];
  const flat = isGrouped
    ? options.flatMap((g) => g.items || [])
    : options.map(String);
  const render = () => {
    if (isGrouped) {
      return options.map((group, gi) => (
        <optgroup key={gi} label={group.group}>
          {(group.items || []).map((opt, i) => (
            <option key={i} value={opt}>
              {opt}
            </option>
          ))}
        </optgroup>
      ));
    }
    return options.map((opt, i) => (
      <option key={i} value={String(opt)}>
        {String(opt)}
      </option>
    ));
  };
  return { flat, isGrouped, render };
}

// Keystroke guard for numeric inputs. Extracted values keep whatever formatting
// the source document used, so this has to accept European "1.486,00" and values
// that carry a currency symbol as well as UK/US "1,486.00" — rejecting them left
// the field unable to accept any edit at all. Letters are still blocked (also via
// onKeyDown); meaning is validated at the export/posting boundary, not per keystroke.
const NUMERIC_INPUT_ALLOWED = /^[\d\s.,+\-€$£¥₹]*$/;

// Numeric line-item columns are right-aligned so decimal points line up down the
// column, matching how NetSuite/BC and every finance document present them.
const NUMERIC_COLUMN_TYPES = new Set([
  "number",
  "integer",
  "currency",
  "money",
  "percentage",
  "percent",
]);
const COMPUTED_COLUMN_TYPES = new Set(["auto", "computed", "formula"]);

function isNumericColumn(col) {
  const type = (col?.type || "text").toLowerCase();
  if (NUMERIC_COLUMN_TYPES.has(type)) return true;
  // A computed column is numeric only when its expression is pure arithmetic —
  // a concatenation like "{code}-{suffix}" stays left-aligned.
  if (COMPUTED_COLUMN_TYPES.has(type)) {
    const expression = col?.expression || "";
    return /[+\-*/]/.test(expression) && /^[{}\w\s+\-*/().]+$/.test(expression);
  }
  return false;
}

// Templates often leave money columns typed as plain text, so fall back to the
// shape of the values. Deliberately narrow: a bare integer like "12345" does NOT
// qualify, otherwise part numbers in a Code column would be right-aligned too.
const DATE_LIKE = /\d{1,4}\s*[./-]\s*\d{1,2}\s*[./-]\s*\d{1,4}/;
const CURRENCY_SYMBOL = /[€$£¥₹]/;
const GROUPED_NUMBER = /^-?\d{1,3}([.,\s]\d{3})*([.,]\d+)?$|^-?\d+[.,]\d+$/;

function looksMonetary(raw) {
  const val = String(raw).trim();
  if (!val || DATE_LIKE.test(val)) return false;
  if (CURRENCY_SYMBOL.test(val)) return /\d/.test(val);
  return GROUPED_NUMBER.test(val) && /[.,]/.test(val);
}

// Array of Objects Sub-component for table-style editing
function ArrayOfObjectsField({ value, onChange, disabled, schema, printed }) {
  const columns = React.useMemo(() => {
    if (!schema) return [];
    if (Array.isArray(schema)) return schema;
    try {
      const parsed = JSON.parse(schema);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return [];
  }, [schema]);

  const parseRows = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const [rows, setRows] = useState(() => parseRows(value));
  const [isExpanded, setIsExpanded] = useState(false);
  // The table as extracted, for "printed" hints under date cells (GitHub #780).
  // Matched by row, so the hints go once rows are added or removed.
  const printedRows = React.useMemo(() => parseRows(printed), [printed]);
  const localChangeRef = React.useRef(false);

  const rightAlignedKeys = React.useMemo(() => {
    const keys = new Set();
    columns.forEach((col) => {
      if (isNumericColumn(col)) {
        keys.add(col.key);
        return;
      }
      const values = rows
        .map((row) => row?.[col.key])
        .filter((v) => v != null && String(v).trim() !== "")
        .map(String);
      const noneAreText =
        values.length > 0 && values.every((v) => !/[a-zA-Z]/.test(v));
      if (noneAreText && values.some(looksMonetary)) keys.add(col.key);
    });
    return keys;
  }, [columns, rows]);

  React.useEffect(() => {
    if (localChangeRef.current) {
      localChangeRef.current = false;
      return;
    }
    setRows(parseRows(value));
  }, [value]);

  const commitChange = (updatedRows) => {
    localChangeRef.current = true;
    setRows(updatedRows);
    onChange(JSON.stringify(updatedRows));
  };

  const handleCellChange = (rowIdx, colKey, newVal) => {
    const updated = rows.map((row, i) =>
      i === rowIdx ? { ...row, [colKey]: newVal } : row,
    );
    commitChange(updated);
  };

  const handleAddRow = () => {
    const emptyRow = {};
    columns.forEach((col) => {
      const t = (col.type || "text").toLowerCase();
      if (t !== "auto" && t !== "computed" && t !== "formula") {
        emptyRow[col.key] = "";
      }
    });
    commitChange([...rows, emptyRow]);
  };

  const handleRemoveRow = (rowIdx) => {
    commitChange(rows.filter((_, i) => i !== rowIdx));
  };

  const renderCellInput = (col, cellValue, rowIdx) => {
    const val = cellValue != null ? String(cellValue) : "";
    const colType = (col.type || "text").toLowerCase();
    const baseClasses = `w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:ring-1 focus:ring-blue-500 focus:border-transparent ${disabled ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-75" : ""}`;
    const numericClasses = `${baseClasses} text-right`;

    switch (colType) {
      case "number":
      case "integer":
        return (
          <input
            type="text"
            inputMode="decimal"
            value={val}
            onChange={(e) => {
              const v = e.target.value;
              if (NUMERIC_INPUT_ALLOWED.test(v)
              )
                handleCellChange(rowIdx, col.key, v);
            }}
            onKeyDown={(e) => {
              if (/^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.metaKey)
                e.preventDefault();
            }}
            disabled={disabled}
            className={numericClasses}
            placeholder={col.label || col.key}
          />
        );
      case "currency":
      case "money": {
        const parts = (val || "").split(" ");
        const symbol = parts[0] || "$";
        const amount = parts.slice(1).join(" ") || "";
        return (
          <div className="flex gap-1">
            <input
              type="text"
              value={symbol}
              onChange={(e) =>
                handleCellChange(
                  rowIdx,
                  col.key,
                  `${e.target.value} ${amount}`.trim(),
                )
              }
              disabled={disabled}
              className={`${baseClasses} w-10 text-center`}
              maxLength="5"
            />
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                const v = e.target.value;
                if (NUMERIC_INPUT_ALLOWED.test(v)
                )
                  handleCellChange(rowIdx, col.key, `${symbol} ${v}`.trim());
              }}
              onKeyDown={(e) => {
                if (/^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.metaKey)
                  e.preventDefault();
              }}
              disabled={disabled}
              className={`${numericClasses} flex-1`}
              placeholder="0.00"
            />
          </div>
        );
      }
      case "percentage":
      case "percent":
        return (
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={val}
              onChange={(e) => {
                const v = e.target.value;
                if (NUMERIC_INPUT_ALLOWED.test(v))
                  handleCellChange(rowIdx, col.key, v);
              }}
              onKeyDown={(e) => {
                if (/^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.metaKey)
                  e.preventDefault();
              }}
              disabled={disabled}
              className={`${numericClasses} pr-5`}
              placeholder={col.label || col.key}
            />
            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]">
              %
            </span>
          </div>
        );
      case "date": {
        const printedCell =
          printedRows.length === rows.length ? printedRows[rowIdx]?.[col.key] : null;
        const showPrinted =
          printedCell != null &&
          String(printedCell).trim() !== "" &&
          String(printedCell).trim() !== val.trim();
        return (
          <div>
            <input
              type={/^\d{4}-\d{2}-\d{2}$/.test(val) ? "date" : "text"}
              value={val}
              onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)}
              disabled={disabled}
              className={baseClasses}
              placeholder="YYYY-MM-DD"
            />
            {showPrinted && (
              <div
                className="mt-0.5 px-1 text-[10px] text-gray-400 dark:text-gray-500 truncate"
                title={`Printed on document: ${printedCell}`}
              >
                Printed: {String(printedCell)}
              </div>
            )}
          </div>
        );
      }
      case "dropdown":
      case "select":
      case "enum": {
        const { flat: flatOpts, render: renderOpts } = parseDropdownOptions(
          col.options || [],
        );
        const valueMatchesOption =
          !val ||
          flatOpts.some((opt) => opt.toLowerCase() === val.toLowerCase());
        return (
          <select
            value={valueMatchesOption ? val : ""}
            onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)}
            disabled={disabled}
            className={`${baseClasses} cursor-pointer`}
          >
            <option value="">Select...</option>
            {renderOpts()}
          </select>
        );
      }
      case "boolean":
      case "bool":
        return (
          <select
            value={val}
            onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)}
            disabled={disabled}
            className={`${baseClasses} cursor-pointer`}
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      case "auto":
      case "computed":
      case "formula": {
        const currentRow = rows[rowIdx] || {};
        const expression = col.expression || "";
        const computed = expression.replace(/\{(\w+)\}/g, (_, key) => {
          const v = currentRow[key];
          return v != null && v !== "" ? String(v) : "";
        });
        const hasContent = computed.replace(/[^a-zA-Z0-9]/g, "").length > 0;
        return (
          <span
            className={`block w-full px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded border border-gray-100 dark:border-gray-700 truncate ${rightAlignedKeys.has(col.key) ? "text-right" : ""}`}
            title={hasContent ? computed : ""}
          >
            {hasContent ? (
              computed
            ) : (
              <span className="italic text-gray-400">—</span>
            )}
          </span>
        );
      }
      case "text":
      default:
        return (
          <input
            type="text"
            value={val}
            onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)}
            disabled={disabled}
            className={
              rightAlignedKeys.has(col.key) ? numericClasses : baseClasses
            }
            placeholder={col.label || col.key}
          />
        );
    }
  };

  if (columns.length === 0) {
    return (
      <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          No column schema defined for this field
        </p>
      </div>
    );
  }

  if (rows.length === 0 && !isExpanded) {
    return (
      <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">
          No items
        </p>
        {!disabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
              handleAddRow();
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            + Add item
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
          {rows.length} item{rows.length !== 1 ? "s" : ""} · {columns.length}{" "}
          column{columns.length !== 1 ? "s" : ""}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </div>
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400 font-medium w-8">
                  #
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-2 py-1.5 ${rightAlignedKeys.has(col.key) ? "text-right" : "text-left"} text-gray-500 dark:text-gray-400 font-medium min-w-[100px]`}
                  >
                    {col.label || col.key}
                  </th>
                ))}
                {!disabled && <th className="px-2 py-1.5 w-8"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="px-2 py-1.5 text-gray-400 font-mono">
                    {rowIdx + 1}
                  </td>
                  {columns.map((col) => (
                    <td key={col.key} className="px-1 py-1">
                      {renderCellInput(col, row[col.key], rowIdx)}
                    </td>
                  ))}
                  {!disabled && (
                    <td className="px-1 py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRow(rowIdx);
                        }}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                        title="Remove row"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!disabled && (
            <div className="flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddRow();
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
              >
                + Add row
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FieldInput – Enhanced field input component with ui_properties support.
 */
function FieldInput({
  field,
  originalField = null,
  learningRule = null,
  aiLearningUsage = null,
  onChange,
  onClick,
  isDate = false,
  isDropdown = false,
  requestId = null,
  onAlternativeSelect = null,
  onViewInDocument = null,
  documents = [],
  disabled = false,
  hasAuditHistoryFromParent = false,
}) {
  // --- Display preferences ---
  const { showConfidenceScore, showAiBadge, showColorCoding } =
    useDisplayPreferences();
  // --- UI Properties from template_field (all keys optional) ---
  const uiProps = field.ui_properties || {};

  const fieldType =
    uiProps.inputType ||
    field.field_type ||
    (isDate ? "date" : isDropdown ? "dropdown" : "text");
  const [value, setValue] = useState(() =>
    sanitizeNumericValue(field.field_value || "", fieldType),
  );
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [alternatives, setAlternatives] = useState([]);
  const [activeFieldData, setActiveFieldData] = useState(null);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const popupRef = React.useRef(null);

  // AI suggestion popover state
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);
  const [aiDismissed, setAiDismissed] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [selectedValueSource, setSelectedValueSource] = useState(
    field.source_type === "ai_learning" ? "ai_learning" : "original",
  );
  const aiSuggestionRef = React.useRef(null);
  // Values a reviewer can choose between for a typed or auto-filled field:
  // the typed value, the automatic value and the AI's reading of the document.
  const valueOptions = useMemo(() => valueCandidates(field), [field]);
  const showAiButton = valueOptions.length >= 2 && !aiDismissed;

  // Audit log state
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const hasAuditHistory = hasAuditHistoryFromParent;
  const auditPopupRef = React.useRef(null);

  const confidence = field.confidence || 0;
  const confidencePercent = Math.round(confidence * 100);
  const alternativeCount = field.alternative_count || 0;
  const hasAlternatives = alternativeCount >= 1;

  const isLowConfidence = confidence < 0.7;
  const isMediumConfidence = confidence >= 0.7 && confidence < 0.9;
  const isHighConfidence = confidence >= 0.9;

  const savedValue = originalField?.field_value ?? "";
  const currentValue = value ?? "";
  const isChangedFromSaved = String(savedValue) !== String(currentValue);

  const hasAiLearningValueKey = Object.prototype.hasOwnProperty.call(
    field,
    "ai_learning_value",
  );

  // Capture immutable baselines for this field instance so source switching
  // does not drift as the editable field value changes.
  const sourceBaselinesRef = React.useRef(null);
  if (!sourceBaselinesRef.current) {
    const baselineOriginal =
      field.source_type === "ai_learning"
        ? (field.extracted_value ?? originalField?.extracted_value ?? "")
        : (field.field_value ?? originalField?.field_value ?? "");

    let baselineAiLearning =
      field.source_type === "ai_learning"
        ? (field.field_value ?? originalField?.field_value ?? "")
        : (field.ai_learning_value ?? originalField?.ai_learning_value ?? "");

    // Fallback for older payload shapes where ai_learning_value may be absent.
    if (!baselineAiLearning && field.source_type === "ai_learning") {
      baselineAiLearning = originalField?.field_value ?? "";
    }

    sourceBaselinesRef.current = {
      original: baselineOriginal,
      aiLearning: baselineAiLearning,
    };
  }

  const originalExtractionValue = sourceBaselinesRef.current.original;
  const aiLearningValue = sourceBaselinesRef.current.aiLearning;
  const showValueSourceToggle =
    field.source_type === "ai_learning" || hasAiLearningValueKey;

  const sourceBadge = (() => {
    if (field.source_type === "ai_learning") {
      return {
        label: "AI Learning",
        className:
          "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
      };
    }
    return fieldSourceBadge(field);
  })();

  useEffect(() => {
    setValue(sanitizeNumericValue(field.field_value || "", fieldType));
  }, [field.field_value, fieldType]);

  useEffect(() => {
    setSelectedValueSource(
      field.source_type === "ai_learning" ? "ai_learning" : "original",
    );
  }, [field.id, field.source_type]);

  const applyValueSource = (source) => {
    const nextRawValue =
      source === "ai_learning" ? aiLearningValue : originalExtractionValue;
    const sanitizedValue = sanitizeNumericValue(nextRawValue || "", fieldType);
    setSelectedValueSource(source);
    setValue(sanitizedValue);
    onChange(field.id, sanitizedValue);
  };

  const fetchFieldAuditLogs = async () => {
    if (!requestId || !field.id) return;
    try {
      setLoadingAuditLogs(true);
      const res = await requestsApi.getFieldAuditLogs(requestId, field.id, {
        limit: 20,
      });
      setAuditLogs(res.data.audit_logs || []);
    } catch (err) {
      console.error("Error fetching field audit logs:", err);
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowAlternatives(false);
        setSelectedPreview(null);
      }
      if (
        auditPopupRef.current &&
        !auditPopupRef.current.contains(event.target)
      ) {
        setShowAuditLog(false);
      }
      if (
        aiSuggestionRef.current &&
        !aiSuggestionRef.current.contains(event.target)
      ) {
        setShowAiSuggestion(false);
      }
    };
    if (showAlternatives || showAuditLog || showAiSuggestion) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAlternatives, showAuditLog, showAiSuggestion]);

  // Must be after all hooks — conditional early return
  if (uiProps.hidden) return null;

  const getDocumentFilename = (fieldData) => {
    if (fieldData?.source_document_filename)
      return fieldData.source_document_filename;
    return "Unknown Source";
  };

  const truncateFilename = (filename, maxLength = 30) => {
    if (!filename || filename.length <= maxLength) return filename;
    const ext = filename.split(".").pop();
    const nameWithoutExt = filename.slice(0, filename.lastIndexOf("."));
    const truncatedName =
      nameWithoutExt.slice(0, maxLength - ext.length - 4) + "...";
    return `${truncatedName}.${ext}`;
  };

  const getAllValues = () => {
    const values = [];
    const currentValue = activeFieldData || {
      id: field.id,
      field_value: field.field_value,
      confidence: field.confidence,
      source_document_id: field.source_document_id,
      source_document_filename: field.source_document_filename,
      page_number: field.page_number,
      bounding_box: field.bounding_box,
    };
    values.push({ ...currentValue, isActive: true });
    alternatives.forEach((alt) => {
      values.push({ ...alt, isActive: false });
    });
    return values;
  };

  const { flat: dropdownOptions, render: renderDropdownOptions } =
    parseDropdownOptions(field.field_values || "");

  const getConfidenceBadgeColor = () => {
    if (!showColorCoding)
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400";
    if (isHighConfidence)
      return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
    if (isMediumConfidence)
      return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
  };

  const getInputBorderColor = () => {
    if (isEmpty) {
      return field.is_required
        ? "border-red-200 bg-red-50/60 dark:border-red-800/60 dark:bg-red-900/10"
        : "border-dashed border-gray-300 bg-gray-50/60 dark:border-gray-600 dark:bg-gray-800/40";
    }
    if (!showColorCoding)
      return "border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800";
    if (isLowConfidence)
      return "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20";
    if (isMediumConfidence)
      return "border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20";
    return "border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800";
  };

  const handleChange = (e) => {
    setValue(e.target.value);
    onChange(field.id, e.target.value);
  };

  const formatFieldName = (name) => {
    if (field.display_name) return field.display_name;
    return name
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const loadAlternatives = async () => {
    if (!requestId || !field.field_name) return;
    setLoadingAlternatives(true);
    try {
      const res = await requestsApi.getFieldAlternatives(
        requestId,
        field.field_name,
      );
      setAlternatives(res.data.alternatives || []);
      setActiveFieldData(res.data.active || field);
    } catch (err) {
      console.error("Error loading alternatives:", err);
    } finally {
      setLoadingAlternatives(false);
    }
  };

  const handleAlternativesClick = async (e) => {
    e.stopPropagation();
    if (!showAlternatives) {
      setShowAlternatives(true);
      loadAlternatives();
    } else {
      setShowAlternatives(false);
      setSelectedPreview(null);
    }
  };

  const handleSelectAlternative = async (alt) => {
    if (onAlternativeSelect) {
      await onAlternativeSelect(alt.id, field.field_name, alt);
    }
    const newValue = alt.field_value || alt.extracted_value || "";
    setValue(sanitizeNumericValue(newValue, fieldType));
    setShowAlternatives(false);
    setSelectedPreview(null);
  };

  const isEmpty = (() => {
    const fv = field.field_value;
    if (!fv) return true;
    if (typeof fv !== "string") return false;
    const trimmed = fv.trim();
    if (trimmed === "") return true;
    if (fieldType === "array_of_objects" || fieldType === "array") {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed.length === 0 : false;
      } catch {
        return false;
      }
    }
    return false;
  })();

  // --- Derived UI overrides from ui_properties ---
  const isReadOnly = uiProps.readOnly || disabled;
  const customPlaceholder = uiProps.placeholder || null;
  const helpText = uiProps.helpText || null;
  const customRows = uiProps.rows || null;
  const prefix = uiProps.prefix || null;
  const suffix = uiProps.suffix || null;
  const labelPosition = uiProps.labelPosition || "top";
  const highlightColor = uiProps.highlight || null;
  const isCompact = uiProps.compact || false;

  const renderInput = () => {
    const sizeClasses = isCompact
      ? "px-3 py-1.5 text-sm"
      : "px-4 py-3 text-base";
    const baseClasses = `w-full ${sizeClasses} font-medium rounded-lg border ${getInputBorderColor()} focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-gray-200 ${isReadOnly ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-75" : ""}`;

    switch (fieldType) {
      case "date": {
        const isValidDateFormat = /^\d{4}-\d{2}-\d{2}$/.test(value);
        return (
          <input
            type={isValidDateFormat ? "date" : "text"}
            value={value}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            className={baseClasses}
            placeholder={customPlaceholder || "Enter date..."}
            disabled={isReadOnly}
          />
        );
      }

      case "number":
      case "integer":
        return (
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              if (NUMERIC_INPUT_ALLOWED.test(v)
              ) {
                setValue(v);
                onChange(field.id, v);
              }
            }}
            onKeyDown={(e) => {
              if (/^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.metaKey)
                e.preventDefault();
            }}
            onClick={(e) => e.stopPropagation()}
            className={baseClasses}
            placeholder={customPlaceholder || ""}
            disabled={isReadOnly}
          />
        );

      case "currency":
      case "money": {
        const parts = (value || "").split(" ");
        const currencySymbol = parts[0] || "$";
        const currencyAmount = parts.slice(1).join(" ") || "";
        return (
          <div className="flex gap-2">
            <input
              type="text"
              value={currencySymbol}
              onChange={(e) => {
                const nv = `${e.target.value} ${currencyAmount}`.trim();
                setValue(nv);
                onChange(field.id, nv);
              }}
              onClick={(e) => e.stopPropagation()}
              className={`${baseClasses} w-20 text-center`}
              placeholder={prefix || "$"}
              maxLength="5"
              disabled={isReadOnly}
            />
            <input
              type="text"
              inputMode="decimal"
              value={currencyAmount}
              onChange={(e) => {
                const na = e.target.value;
                if (NUMERIC_INPUT_ALLOWED.test(na)
                ) {
                  const nv = `${currencySymbol} ${na}`.trim();
                  setValue(nv);
                  onChange(field.id, nv);
                }
              }}
              onKeyDown={(e) => {
                if (/^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.metaKey)
                  e.preventDefault();
              }}
              onClick={(e) => e.stopPropagation()}
              className={`${baseClasses} flex-1`}
              placeholder={customPlaceholder || "0.00"}
              disabled={isReadOnly}
            />
          </div>
        );
      }

      case "dropdown":
      case "select":
      case "enum": {
        const valueMatchesOption =
          !value ||
          dropdownOptions.some(
            (opt) => opt.toLowerCase() === (value || "").toLowerCase(),
          );
        return (
          <select
            value={valueMatchesOption ? value : ""}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            className={`${baseClasses} pr-12 cursor-pointer`}
            disabled={isReadOnly}
          >
            <option value="">{customPlaceholder || "Select..."}</option>
            {renderDropdownOptions()}
          </select>
        );
      }

      case "textarea":
      case "text_area":
      case "multiline":
        return (
          <textarea
            value={value}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            rows={customRows || 3}
            className={`${baseClasses} resize-none`}
            placeholder={customPlaceholder || ""}
            disabled={isReadOnly}
          />
        );

      case "percentage":
      case "percent":
        return (
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(e) => {
                const v = e.target.value;
                if (NUMERIC_INPUT_ALLOWED.test(v)) {
                  setValue(v);
                  onChange(field.id, v);
                }
              }}
              onKeyDown={(e) => {
                if (/^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.metaKey)
                  e.preventDefault();
              }}
              onClick={(e) => e.stopPropagation()}
              className={`${baseClasses} pr-8`}
              placeholder={customPlaceholder || ""}
              disabled={isReadOnly}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
              {suffix || "%"}
            </span>
          </div>
        );

      case "array_of_objects":
      case "array":
        return (
          <ArrayOfObjectsField
            value={value}
            printed={field.is_manually_edited ? undefined : field.extracted_value}
            schema={field.field_values}
            onChange={(newVal) => {
              setValue(newVal);
              onChange(field.id, newVal);
            }}
            disabled={isReadOnly}
          />
        );

      case "boolean":
      case "bool":
        return (
          <select
            value={value}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            className={`${baseClasses} pr-12 cursor-pointer`}
            disabled={isReadOnly}
          >
            <option value="">{customPlaceholder || "Select..."}</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      case "text":
      default:
        if (prefix || suffix) {
          return (
            <div className="flex items-center gap-0">
              {prefix && (
                <span
                  className={`${isCompact ? "px-2 py-1.5 text-sm" : "px-3 py-3 text-base"} font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-r-0 border-gray-200 dark:border-gray-600 rounded-l-lg`}
                >
                  {prefix}
                </span>
              )}
              <input
                type="text"
                value={value}
                onChange={handleChange}
                onClick={(e) => e.stopPropagation()}
                className={`${baseClasses} ${isEmpty ? "italic" : ""} ${prefix ? "rounded-l-none" : ""} ${suffix ? "rounded-r-none" : ""}`}
                placeholder={
                  customPlaceholder || (isEmpty ? "Enter value..." : "")
                }
                disabled={isReadOnly}
              />
              {suffix && (
                <span
                  className={`${isCompact ? "px-2 py-1.5 text-sm" : "px-3 py-3 text-base"} font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-l-0 border-gray-200 dark:border-gray-600 rounded-r-lg`}
                >
                  {suffix}
                </span>
              )}
            </div>
          );
        }
        return (
          <input
            type="text"
            value={value}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            className={`${baseClasses} ${isEmpty ? "italic" : ""}`}
            placeholder={customPlaceholder || (isEmpty ? "Enter value..." : "")}
            disabled={isReadOnly}
          />
        );
    }
  };

  const highlightBorderStyle =
    highlightColor && showColorCoding
      ? {
          borderLeft: `3px solid ${highlightColor.startsWith("#") ? highlightColor : `var(--color-${highlightColor}, ${highlightColor})`}`,
        }
      : {};

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer relative group ${highlightColor && showColorCoding ? "pl-2" : ""}`}
      style={highlightBorderStyle}
    >
      {/* Field Label Row */}
      <div
        className={`flex items-center justify-between gap-2 ${labelPosition === "inline" ? "mb-0" : "mb-1.5"}`}
      >
        <div className="flex items-center gap-1.5">
          {showColorCoding && (
            <div
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${isHighConfidence ? "bg-green-500" : isMediumConfidence ? "bg-orange-500" : "bg-red-500"}`}
            ></div>
          )}
          <label
            className={`${isCompact ? "text-[10px]" : "text-xs"} font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap`}
          >
            {formatFieldName(field.field_name)}
            {field.is_required && (
              <span className="text-red-500 ml-0.5" title="Required field">
                *
              </span>
            )}
          </label>
          {showConfidenceScore && !isEmpty && (
            <span
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${getConfidenceBadgeColor()}`}
            >
              {confidencePercent}%
            </span>
          )}
          {showAiBadge && !isEmpty && field.source_type === "llm_fallback" && (
            <span
              className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
              style={{
                backgroundColor: "color-mix(in srgb, var(--ai-gradient-from) 15%, transparent)",
                color: "var(--ai-gradient-from)",
              }}
              title="Extracted by AI (LLM fallback)"
            >
              AI
            </span>
          )}
          {sourceBadge && !isEmpty && (
            <span
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${sourceBadge.className}`}
              title={sourceBadge.title}
            >
              {sourceBadge.label}
            </span>
          )}
          {field.is_manually_edited && !isEmpty && (
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
              Edited
            </span>
          )}
          {isEmpty && (
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-medium italic ${field.is_required ? "text-red-400 dark:text-red-400/80" : "text-gray-400 dark:text-gray-500"}`}
            >
              {field.is_required && (
                <span className="h-1 w-1 rounded-full bg-red-400 not-italic shrink-0" />
              )}
              Not extracted
            </span>
          )}
          {showColorCoding &&
            showConfidenceScore &&
            !isEmpty &&
            isLowConfidence &&
            !hasAlternatives && (
              <span className="text-xs text-red-500 dark:text-red-400 italic">
                Low confidence - manual review required
              </span>
            )}
        </div>

        <div className="flex items-center gap-1">
          {/* Compare Values button — shown for user-input fields that also have AI extraction */}
          {showAiButton && (
            <div className="relative" ref={aiSuggestionRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAiSuggestion(!showAiSuggestion);
                }}
                className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all border ${
                  showAiSuggestion
                    ? "bg-blue-600 text-white border-blue-600 shadow-md"
                    : "text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300"
                }`}
                title="Compare: see every value for this field and where it came from"
              >
                <Sparkles className="h-3 w-3" />
                <span>Compare</span>
              </button>

              {showAiSuggestion && (
                <div
                  className="absolute z-50 mt-1.5 right-0 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                  style={{ width: "320px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-blue-500" /> Values for
                      this field
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDismissConfirm(true);
                          setShowAiSuggestion(false);
                        }}
                        className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-0.5"
                        title="Don't show again for this field"
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAiSuggestion(false);
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-0.5"
                        title="Close"
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-2.5 space-y-1.5">
                    {(() => {
                      const active = activeCandidate(valueOptions, field, value);
                      return valueOptions.map((option) => {
                        const inUse = active?.key === option.key;
                        return (
                          <div
                            key={option.key}
                            className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border ${
                              inUse
                                ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700"
                                : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="mb-1 flex items-center gap-1.5 min-w-0">
                                <span
                                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${option.className}`}
                                  title={option.title}
                                >
                                  {option.label}
                                </span>
                                {option.detail && (
                                  <span
                                    className="text-[10px] text-gray-500 dark:text-gray-400 truncate"
                                    title={option.detail}
                                  >
                                    {option.detail}
                                  </span>
                                )}
                              </p>
                              <p
                                className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate"
                                title={option.value}
                              >
                                {option.value}
                              </p>
                            </div>
                            {inUse ? (
                              <span
                                className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300 flex-shrink-0"
                                title={
                                  active.formatted
                                    ? `In use, formatted as ${value}`
                                    : "This is the field's value"
                                }
                              >
                                {active.formatted ? "In use · formatted" : "In use"}
                              </span>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setValue(option.value);
                                  onChange(field.id, option.value);
                                  setShowAiSuggestion(false);
                                }}
                                disabled={disabled}
                                className="text-xs font-medium px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex-shrink-0 transition-colors"
                              >
                                Use this
                              </button>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* Dismiss confirmation popup */}
              {showDismissConfirm && (
                <div
                  className="absolute z-50 mt-1.5 right-0 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3"
                  style={{ width: "240px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="text-xs text-gray-700 dark:text-gray-300 mb-2.5">
                    Hide the Compare button for this field? You won't see the
                    alternative value anymore.
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDismissConfirm(false);
                      }}
                      className="text-xs font-medium px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAiDismissed(true);
                        setShowDismissConfirm(false);
                      }}
                      className="text-xs font-medium px-2.5 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
                    >
                      Hide
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!showAuditLog) fetchFieldAuditLogs();
              setShowAuditLog(!showAuditLog);
            }}
            className={`flex-shrink-0 p-1.5 rounded-md transition-all ${showAuditLog ? "text-gray-700 dark:text-gray-200 font-bold" : hasAuditHistory ? "text-gray-500 hover:bg-gray-100 hover:text-gray-700" : "text-gray-400 hover:bg-gray-50 hover:text-gray-500"}`}
            title="View edit history"
          >
            <History className="h-3.5 w-3.5" strokeWidth={showAuditLog ? 2.75 : 2} />
          </button>
          {hasAlternatives && !disabled && (
            <button
              onClick={handleAlternativesClick}
              className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all border ${showAlternatives ? "bg-blue-600 text-white border-blue-600 shadow-md" : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"}`}
              title={`${alternativeCount} alternative value(s) from different documents`}
            >
              <Layers className="h-3 w-3" />
              <span>{alternativeCount + 1}</span>
              <ChevronDown
                className={`h-2.5 w-2.5 transition-transform ${showAlternatives ? "rotate-180" : ""}`}
              />
            </button>
          )}
        </div>
      </div>

      {showValueSourceToggle && (
        <div className="mb-1.5 flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Value Source
          </span>
          <button
            type="button"
            disabled={isReadOnly}
            onClick={(e) => {
              e.stopPropagation();
              applyValueSource("original");
            }}
            className={`px-2 py-1 rounded-md text-[10px] border transition-colors ${
              selectedValueSource === "original"
                ? "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700"
                : "bg-white text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
            } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
            title={`AI extraction: ${String(originalExtractionValue || "(empty)")}`}
          >
            AI extraction
          </button>
          <button
            type="button"
            disabled={isReadOnly}
            onClick={(e) => {
              e.stopPropagation();
              applyValueSource("ai_learning");
            }}
            className={`px-2 py-1 rounded-md text-[10px] border transition-colors ${
              selectedValueSource === "ai_learning"
                ? "bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700"
                : "bg-white text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
            } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
            title={`AI learning: ${String(aiLearningValue || "(empty)")}`}
          >
            AI Learning
          </button>
        </div>
      )}

      {/* Input with field value */}
      <div className={`${labelPosition === "inline" ? "flex-1" : ""} relative`}>
        {renderInput()}
        {fieldType === "date" && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700/60">
            <svg
              className="h-4 w-4 text-gray-500 dark:text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="18"
                rx="3"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path d="M3 9.5h18" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M8 2.5v3M16 2.5v3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <circle cx="8.5" cy="14" r="1" fill="currentColor" />
              <circle cx="12" cy="14" r="1" fill="currentColor" />
              <circle cx="15.5" cy="14" r="1" fill="currentColor" />
              <circle cx="8.5" cy="17.5" r="1" fill="currentColor" />
              <circle cx="12" cy="17.5" r="1" fill="currentColor" />
            </svg>
          </div>
        )}
        {(fieldType === "dropdown" ||
          fieldType === "select" ||
          fieldType === "enum" ||
          fieldType === "boolean" ||
          fieldType === "bool") && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700/60">
            <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </div>
        )}
      </div>

      {/* Help text from ui_properties */}
      {helpText && (
        <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500 italic">
          {helpText}
        </p>
      )}

      {isEmpty && isEmailSourced(field.template_field) && (
        <p className="mt-1 text-[10px] text-violet-600 dark:text-violet-300">
          Filled from the e-mail this request came from, but this request
          doesn&apos;t have it. Enter it manually if it&apos;s needed.
        </p>
      )}

      {field.is_manually_edited && field.extracted_value && valueOptions.length < 2 && (
        <div className="mt-1.5 rounded-md border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 px-2.5 py-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
            Original extracted
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5 break-words">
            {String(field.extracted_value)}
          </p>
        </div>
      )}

      {/* The date as printed, when it was stored differently (GitHub #780), so
          the reviewer can check how it was read without opening the document. */}
      {fieldType === "date" &&
        !field.is_manually_edited &&
        String(field.extracted_value ?? "").trim() !== "" &&
        String(field.extracted_value).trim() !== String(field.field_value ?? "").trim() && (
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 break-words">
            Printed on document:{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {String(field.extracted_value)}
            </span>
          </p>
        )}

      {field.needs_review && field.review_reason && !isChangedFromSaved && (
        <div className="mt-1.5 rounded-md border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/15 px-2.5 py-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
            Check this value
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5 break-words">
            {field.review_reason}
          </p>
        </div>
      )}

      {isChangedFromSaved && (
        <div className="mt-1.5 rounded-md border border-navy-200 dark:border-navy-800/40 bg-navy-50 dark:bg-navy-900/10 px-2.5 py-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-navy-700 dark:text-navy-300">
            Pending save
          </p>
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-0.5 break-words">
            Saved:{" "}
            <span className="font-medium">
              {String(savedValue || "(empty)")}
            </span>
          </p>
          <p className="text-[11px] text-gray-700 dark:text-gray-200 break-words">
            Current:{" "}
            <span className="font-semibold">
              {String(currentValue || "(empty)")}
            </span>
          </p>
        </div>
      )}

      {/* Constraint violation alert */}
      {(() => {
        if (!field.validation_rules || !value) return null;
        try {
          const rules =
            typeof field.validation_rules === "string"
              ? JSON.parse(field.validation_rules)
              : field.validation_rules;
          if (
            ["number", "currency", "percentage", "integer", "percent"].includes(
              fieldType,
            )
          ) {
            // For currency, extract the numeric part (skip currency symbol)
            let numericValue = value;
            if (fieldType === "currency" || fieldType === "money") {
              const parts = (value || "").split(" ");
              numericValue = parts.slice(1).join(" ") || parts[0];
            }
            const num = parseFloat(String(numericValue).replace(/,/g, ""));
            if (isNaN(num)) return null;
            const hasMin = rules.min != null;
            const hasMax = rules.max != null;
            if (hasMin && num < rules.min) {
              return (
                <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 text-[10px] font-bold text-center leading-[14px]">
                    !
                  </span>
                  Value {num} is below the expected minimum ({rules.min})
                </p>
              );
            }
            if (hasMax && num > rules.max) {
              return (
                <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 text-[10px] font-bold text-center leading-[14px]">
                    !
                  </span>
                  Value {num} exceeds the expected maximum ({rules.max})
                </p>
              );
            }
          }
          return null;
        } catch {
          return null;
        }
      })()}

      {/* Alternatives Popup */}
      {showAlternatives && (
        <div
          ref={popupRef}
          className="absolute z-50 mt-2 right-0 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ width: "340px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Select value{" "}
              <span className="text-gray-400">
                ({alternatives.length + 1} found)
              </span>
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAlternatives(false);
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {loadingAlternatives ? (
            <div className="p-6 text-center">
              <RefreshCw className="h-4 w-4 animate-spin mx-auto text-blue-500" />
              <span className="text-xs text-gray-500 mt-2 block">
                Loading...
              </span>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
              {getAllValues().map((item) => (
                <div
                  key={item.id}
                  className={`px-3 py-2.5 transition-all cursor-pointer flex items-center gap-3 ${item.isActive ? "bg-green-50 dark:bg-green-900/30" : selectedPreview?.id === item.id ? "bg-blue-50 dark:bg-blue-900/30" : "hover:bg-gray-50 dark:hover:bg-gray-800"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreview(item);
                    if (onViewInDocument && item.source_document_id)
                      onViewInDocument(
                        item.source_document_id,
                        item.page_number || 1,
                        item.bounding_box,
                      );
                  }}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.isActive ? "border-green-500 bg-green-500" : "border-gray-300 hover:border-blue-500"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!item.isActive) handleSelectAlternative(item);
                    }}
                  >
                    {item.isActive && (
                      <Check className="h-2.5 w-2.5 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm truncate ${item.isActive ? "font-medium text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"}`}
                    >
                      {item.field_value || item.extracted_value || "(empty)"}
                    </p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">
                      {truncateFilename(getDocumentFilename(item), 35)}
                      {item.page_number && (
                        <span className="ml-1">• p.{item.page_number}</span>
                      )}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${(item.confidence || 0) >= 0.9 ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : (item.confidence || 0) >= 0.7 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}
                  >
                    {Math.round((item.confidence || 0) * 100)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Audit History Popup */}
      {showAuditLog && (
        <AnchoredPopover
          ref={auditPopupRef}
          anchorClassName="right-0"
          className="bg-white dark:bg-gray-900 rounded-xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 border border-gray-100 dark:border-gray-700 overflow-hidden"
          style={{ width: "380px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3.5 py-2.5 bg-gradient-to-r from-navy-600 to-navy-700 flex items-center justify-between">
            <span className="text-xs font-semibold text-white flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Edit History
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAuditLog(false);
              }}
              className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded-md transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {loadingAuditLogs ? (
            <div className="p-6 text-center">
              <Loader2 className="h-4 w-4 animate-spin mx-auto text-navy-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">
                Loading history...
              </span>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="p-6 text-center">
              <History className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-1.5" />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No edit history found
              </p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-72 divide-y divide-gray-100 dark:divide-gray-700">
              {auditLogs.map((log, idx) => {
                let oldValue = null,
                  newValue = null;
                try {
                  const changes =
                    typeof log.audit_json === "string"
                      ? JSON.parse(log.audit_json)
                      : log.audit_json;
                  Object.keys(changes || {}).forEach((key) => {
                    if (
                      changes[key].old !== undefined ||
                      changes[key].new !== undefined
                    ) {
                      oldValue = changes[key].old;
                      newValue = changes[key].new;
                    }
                  });
                } catch (e) {
                  console.error("Error parsing audit_json:", e);
                }
                const actorName =
                  log.created_by_name || log.created_by || "System";
                const actorInitial = actorName.trim().charAt(0).toUpperCase() || "?";
                return (
                  <div
                    key={log.id || idx}
                    className="px-3.5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-navy-100 dark:bg-navy-900/50 text-navy-700 dark:text-navy-300 text-[10px] font-bold flex items-center justify-center">
                        {actorInitial}
                      </span>
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">
                        {actorName}
                      </span>
                      <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        {log.created_at ? formatDateTime(log.created_at) : ""}
                      </span>
                    </div>
                    <div className="space-y-1 pl-7">
                      {oldValue !== null && oldValue !== undefined && (
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 w-8 flex-shrink-0 pt-0.5">
                            From
                          </span>
                          <span className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 px-2 py-0.5 rounded-md break-all">
                            {String(oldValue) || "(empty)"}
                          </span>
                        </div>
                      )}
                      {newValue !== null && newValue !== undefined && (
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 w-8 flex-shrink-0 pt-0.5">
                            To
                          </span>
                          <span className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 px-2 py-0.5 rounded-md break-all">
                            {String(newValue) || "(empty)"}
                          </span>
                        </div>
                      )}
                      {log.reason && (
                        <div className="pt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                          "{log.reason}"
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </AnchoredPopover>
      )}
    </div>
  );
}

export default RequestDetail;
